import {
  createDatabaseShardManifest,
  createDatabaseShardManifestEntry,
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseBlockModel,
  type DatabaseShardManifest,
  type DatabaseShardManifestEntry,
} from '@blocksuite/affine-model';
import { WriterInfoProvider } from '@blocksuite/affine-shared/services';
import { type InsertToPosition } from '@blocksuite/affine-shared/utils';
import { combineRejections } from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { createYProxy, StoreHistoryOverride } from '@blocksuite/store';
import {
  assertDatabaseCellWithinTypeLimit,
  assertDatabaseRowCountWithinLimit,
  assertDatabaseRowWithinByteBudget,
  canonicalDatabaseRowOrder,
  classifyPageSubdocId,
  createDatabaseRowCreationSystemFieldCells,
  createDatabaseRowUpdateSystemFieldCells,
  DATABASE_LARGE_DATA_POLICY,
  DATABASE_ROW_CREATED_BY_FIELD_ID,
  DATABASE_ROW_CREATED_TIME_FIELD_ID,
  DATABASE_ROW_UPDATED_BY_FIELD_ID,
  DatabaseBulkBudgetError,
  databaseCurrentCellKeys,
  databaseLineageColumns,
  databaseRowCellsAfterWrite,
  databaseRowFingerprintProbe,
  databaseRowShardId,
  databaseShardAddedRowIds,
  databaseShardDocWriteClocks,
  databaseShardMayContainProbe,
  type DatabaseShardRowWrite,
  databaseShardRowWriteCondenseDue,
  databaseShardsMayContainRow,
  type DatabaseShardValueColumn,
  databaseShardValueColumns,
  DOCUMENT_PRODUCT_HARD_LIMIT_BYTES,
  FILE_INDEX_COLUMNS,
  markDatabaseShardCondenseDue,
  nextDatabaseShardIndex,
  normalizeDatabaseDeletedColumns,
  pageBlockIdsOfKind,
  pageReadSource,
  partitionDatabaseRowShardRows,
  planDatabaseRowInsertion,
  planDatabaseRowOrderSplices,
  planDatabaseRowShardMerges,
  planDatabaseShardManifestChanges,
  projectDatabaseRowCells,
  readDatabaseCellAs,
  readDatabaseShardDirectoryPartDocIds,
  readDatabaseShardDirectoryShardPartDocIds,
  recordDatabaseShardRowWrite,
  removableDeletedColumnIds,
  selectDatabaseShardsMayContainRow,
  selectDatabaseShardsMayContainRows,
  summarizeDatabaseShardValueKeys,
  toDatabaseRowStoredCell,
} from '@streamlain/database-identity';
import {
  type LocalPublicationReceipt,
  PublicationError,
  type PublicationMemberBase,
} from '@streamlain/doc-publication-contract';
import { localPublicationApplyOrigin } from '@streamlain/doc-publication-contract';
import type {
  DatabaseImportCommit,
  DatabaseImportCommitSummary,
  DatabaseImportPort,
  DatabaseRowChanges,
  DatabaseRowEdit,
  DatabaseRowsWritten,
  DatabaseRowTransactionPort,
} from '@streamlain/doc-publication-contract/database-writer';
import {
  applyDatabaseRowIntent,
  planDatabaseRowShardMergeEdits,
} from '@streamlain/doc-publication-contract/database-writer';
import {
  applyUpdate,
  Array as YArray,
  Doc as YDoc,
  encodeStateAsUpdate,
  Map as YMap,
} from 'yjs';

import {
  type DatabaseLocalRowChange,
  type DatabasePublicationScope,
  type DatabaseRelationInverseEdit,
  type DatabaseRowCellValueInsert,
  type DatabaseRowMutation,
  type DatabaseRowSet,
  type DatabaseRowShardDocLoadReason,
  type DataSourceWriteActor,
  type RawValueJsonConverter,
  type RowShardCell,
  type RowShardCells,
} from './data-source-contracts.js';
import {
  type DatabaseComputePort,
  databaseComputePort,
  measureDatabaseCompute,
  measureDatabaseRowsWithKernel,
  reconcileDatabaseRowsWithKernel,
} from './data-source-crdt-compute.js';
import {
  publicationDirectoryPartMaps,
  publicationStageDocs,
  readPublicationDirectory,
  readPublicationDirectoryPartDocIds,
  writeDatabaseRootDirectory,
} from './data-source-directory-write.js';
import {
  applyDatabaseSemanticChange,
  DatabaseMutationHistory,
  diffDatabaseSemantics,
  snapshotDatabaseSemantics,
} from './data-source-history.js';
import {
  applyDatabaseLivePublication,
  type DatabaseLivePublicationMember,
} from './data-source-live-publication.js';
import {
  DatabasePublicationStage,
  getDatabaseCreationStage,
} from './data-source-publication.js';
import type {
  DatabaseShardDocument,
  DatabaseSourceRuntime,
} from './data-source-runtime-context.js';
import {
  onShardDirectoryComplete,
  requireCompleteShardDirectory,
  requireShardDirectoryRowCount,
} from './data-source-shard-directory.js';
import {
  fromJsonValue,
  INITIAL_DATABASE_ROW_WINDOW_LIMIT,
  isReadonlyYMap,
  normalizeRowShardCell,
  SCHEMA_LOCKED_INVALID_CELL_VALUE,
} from './data-source-values.js';
import { normalizeAttachmentCell } from './file-index-cell-values.js';
import {
  getFileIndexShardRegistry,
  getFileIndexWriterShardPrefix,
  isSameFileIndexShardEntry,
  nextFileIndexWriterShardId,
  observeFileIndexShardRegistry,
  projectFileIndexShardEntries,
  readFileIndexShardEntries,
  selectFileIndexWriterShard,
  sortFileIndexShardEntries,
  writeFileIndexShardEntry,
} from './file-index-shard-registry.js';
import {
  ensurePendingCreatedRows,
  type PendingCreatedRow,
  pendingCreatedRowsOf,
  type PendingRowFollowup,
  readPendingRowUpdateCells,
  recordPendingRowUpdate,
} from './pending-created-rows.js';

/** Server-side row shard numbering starts here (`...$rows$00001`). */
const INITIAL_ROW_SHARD_ID = '00001';

export function initializeFileIndexShardRegistry(
  this: DatabaseSourceRuntime
): void {
  if (!this.isFileIndexDatabase()) return;
  onceDirectoryComplete(this, 'file-index-registry', manifest => {
    const registry = getFileIndexShardRegistry(
      this._model.store.doc.spaceDoc,
      this.databaseId
    );
    const seededEntries = new Map(
      readFileIndexShardEntries(registry).map(shard => [shard.shardId, shard])
    );
    for (const shard of manifest.shards) {
      if (!seededEntries.has(shard.shardId)) {
        seededEntries.set(shard.shardId, shard);
      }
    }
    const seededShards = sortFileIndexShardEntries(seededEntries.values());
    const writerPrefix = getFileIndexWriterShardPrefix(
      this._hostModel.store.doc.spaceDoc
    );
    this._fileIndexWriterShardId = selectFileIndexWriterShard(
      seededShards,
      writerPrefix,
      this._fileIndexWriterShardId,
      manifest.targetBytes
    )?.shardId;
    this.assertFileIndexRootSchemaWithinLimits({
      manifest: createDatabaseShardManifest({
        ...manifest,
        shards: seededShards,
      }),
      registryShards: seededShards,
    });

    this._fileIndexShardRegistryDisposer = observeFileIndexShardRegistry(
      registry,
      () => this.publishLocalShardManifest([])
    );
    this.publishLocalShardManifest(seededShards);
  });
}

export function publishLocalShardManifest(
  this: DatabaseSourceRuntime,
  shards: DatabaseShardManifestEntry[]
): void {
  const previous = requireCompleteShardDirectory(this._model, this.databaseId);
  let canonicalShards = shards;
  const registry = this.isFileIndexDatabase()
    ? getFileIndexShardRegistry(this._model.store.doc.spaceDoc, this.databaseId)
    : undefined;
  if (registry) {
    canonicalShards = projectFileIndexShardEntries(
      readFileIndexShardEntries(registry),
      shards
    );
  }
  const manifestOf = (entries: DatabaseShardManifestEntry[]) =>
    createDatabaseShardManifest({
      databaseId: previous.databaseId,
      databaseRootDocId: previous.databaseRootDocId,
      workspaceId: previous.workspaceId,
      targetBytes: previous.targetBytes,
      hardLimitBytes: this.isFileIndexDatabase()
        ? DOCUMENT_PRODUCT_HARD_LIMIT_BYTES
        : previous.hardLimitBytes,
      shards: entries,
    });
  const manifest = manifestOf(canonicalShards);
  this.assertFileIndexRootSchemaWithinLimits({
    manifest,
    registryShards: canonicalShards,
  });
  const registryChanged =
    registry &&
    canonicalShards.some(
      shard => !isSameFileIndexShardEntry(registry.get(shard.shardId), shard)
    );
  if (
    registryChanged ||
    planDatabaseShardManifestChanges(previous, manifest).length > 0
  ) {
    queueDatabaseRootMutation(
      this,
      model => {
        const stagedRegistry = registry
          ? getFileIndexShardRegistry(model.store.doc.spaceDoc, this.databaseId)
          : undefined;
        if (!stagedRegistry) {
          writeModelDirectory(this, model, manifest);
          return;
        }
        // The mutation runs after other writes to the registry: it writes only
        // the given entries and projects the directory from the registry as it
        // finds it, never entries read when it was queued.
        for (const shard of shards)
          if (
            !isSameFileIndexShardEntry(stagedRegistry.get(shard.shardId), shard)
          )
            writeFileIndexShardEntry(stagedRegistry, shard);
        writeModelDirectory(
          this,
          model,
          manifestOf(
            projectFileIndexShardEntries(
              readFileIndexShardEntries(stagedRegistry),
              []
            )
          )
        );
      },
      'schema',
      false
    );
  }
}

type DirectoryCompletePurpose =
  | 'file-index-registry'
  | 'file-index-observed-manifest';

/** Independent purposes share the directory subscription mechanism. */
const directoryCompleteWaits = new WeakMap<
  DatabaseSourceRuntime,
  Map<DirectoryCompletePurpose, () => void>
>();

/** Runs a purpose once the directory is complete; missing parts end its wait. */
export function onceDirectoryComplete(
  runtime: DatabaseSourceRuntime,
  purpose: DirectoryCompletePurpose,
  run: (manifest: DatabaseShardManifest) => void
): void {
  if (runtime._disposed) return;
  const waits = directoryCompleteWaits.get(runtime) ?? new Map();
  directoryCompleteWaits.set(runtime, waits);
  if (waits.has(purpose)) return;
  let completed = false;
  const stop = onShardDirectoryComplete(
    {
      getCompleteShardDirectory: () => runtime.getCompleteShardDirectory(),
      onShardedRowsChange: onChange => {
        let initial = true;
        return runtime._shardedRowsRevision.subscribe(() => {
          if (initial) initial = false;
          else onChange();
        });
      },
    },
    directory => {
      completed = true;
      waits.delete(purpose);
      if (!waits.size) directoryCompleteWaits.delete(runtime);
      if (!runtime._disposed && directory.status === 'ready')
        run(directory.manifest);
    }
  );
  if (!completed) waits.set(purpose, stop);
}

/** Ends all directory waits of a disposed data source. */
export function disposeDirectoryCompleteWait(
  runtime: DatabaseSourceRuntime
): void {
  directoryCompleteWaits.get(runtime)?.forEach(stop => stop());
  directoryCompleteWaits.delete(runtime);
}

/**
 * Cloud database roots always carry at least one physical row shard; the
 * sync layer never pushes a `shards: []` root (hydration placeholder). A
 * freshly created database therefore gets its first shard right away,
 * otherwise a database without rows keeps a system-doc binding whose root
 * never reaches the server. Idempotent once the root carries a shard.
 */
export function provisionInitialShard(this: DatabaseSourceRuntime): void {
  const manifest = requireCompleteShardDirectory(this._model, this.databaseId);
  if (manifest.shards.length > 0) return;
  if (!this.isFileIndexDatabase()) {
    const shard = this.createLocalShardEntry(INITIAL_ROW_SHARD_ID);
    this.ensureShardDocsLoaded(shard, 'write');
    this.publishLocalShardManifest([shard]);
    return;
  }
  const registry = getFileIndexShardRegistry(
    this._model.store.doc.spaceDoc,
    this.databaseId
  );
  if (registry.size > 0) {
    // A re-provisioned storage block inherits the writer shards already
    // registered in this root doc; projecting them keeps their rows.
    this.publishLocalShardManifest([]);
    return;
  }
  const shard = this.createLocalShardEntry(
    nextFileIndexWriterShardId(this._model.store.doc.spaceDoc, manifest.shards)
  );
  this._fileIndexWriterShardId = shard.shardId;
  this.ensureShardDocsLoaded(shard, 'write');
  this.publishLocalShardManifest([shard]);
}

export function createLocalShardEntry(
  this: DatabaseSourceRuntime,
  shardId: string
): DatabaseShardManifestEntry {
  return createDatabaseShardManifestEntry({
    databaseId: this.databaseId,
    workspaceId: this._model.store.workspace.id,
    shardId,
    firstRowId: undefined,
    lastRowId: undefined,
    rowCount: 0,
    encodedBytes: 0,
    hardLimitBytes: this.isFileIndexDatabase()
      ? DOCUMENT_PRODUCT_HARD_LIMIT_BYTES
      : DATABASE_LARGE_DATA_POLICY.shard.hardLimitBytes,
    status: 'ready',
  });
}

/**
 * The entry of `shard` after it holds `rowIds`. `write` names the rows a
 * write brought in and the clock its shard document reached; without it the
 * fingerprints and write clocks stay as they are.
 */
export function updateLocalShardManifestEntry(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  rowIds: string[],
  encodedBytes: number,
  valueKeys: readonly string[] | undefined = shard.valueKeys,
  write?: DatabaseShardRowWrite
): DatabaseShardManifestEntry {
  const recorded = write
    ? recordDatabaseShardRowWrite(shard, rowIds, write)
    : shard;
  return createDatabaseShardManifestEntry({
    databaseId: this.databaseId,
    workspaceId: this._model.store.workspace.id,
    shardId: shard.shardId,
    rowFingerprints: recorded.rowFingerprints,
    rowWriteClocks: recorded.rowWriteClocks,
    rowFingerprintSeq: shard.rowFingerprintSeq,
    firstRowId: rowIds[0],
    lastRowId: rowIds.at(-1),
    rowCount: rowIds.length,
    encodedBytes,
    hardLimitBytes: this.isFileIndexDatabase()
      ? DOCUMENT_PRODUCT_HARD_LIMIT_BYTES
      : DATABASE_LARGE_DATA_POLICY.shard.hardLimitBytes,
    status: 'ready',
    valueKeys,
  });
}

/** Columns whose values shard entries summarize. */
function shardValueColumns(
  context: DatabaseSourceRuntime
): DatabaseShardValueColumn[] {
  return databaseShardValueColumns(context._model.props.columns);
}

/**
 * Internal metadata-only cost probe; never loads unrelated row payloads.
 * Returns every shard that may hold each row: a complete local snapshot
 * answers exactly, otherwise the row fingerprints answer, and a fingerprint
 * false positive only adds a candidate the caller loads and rules out.
 */
export function findLocalRowShards(
  this: DatabaseSourceRuntime,
  rowIds: readonly string[]
): ReadonlyMap<string, readonly DatabaseShardManifestEntry[]> {
  if (this.readonly$.value) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Cannot write a readonly database.'
    );
  }
  const manifest = requireCompleteShardDirectory(this._model, this.databaseId);
  const wanted = new Map(
    rowIds.map(rowId => [rowId, databaseRowFingerprintProbe(rowId)] as const)
  );
  const candidates = new Map<string, DatabaseShardManifestEntry[]>();
  if (!wanted.size) return candidates;
  for (const shard of manifest.shards) {
    const doc = this.getExistingShardDoc(shard);
    const order =
      doc && this.hasCompleteLoadedShardRows(shard, doc)
        ? new Set(
            doc.spaceDoc
              .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
              .toArray()
              .map(String)
          )
        : undefined;
    for (const [rowId, probe] of wanted) {
      if (
        order ? !order.has(rowId) : !databaseShardMayContainProbe(shard, probe)
      )
        continue;
      const shards = candidates.get(rowId);
      if (shards) shards.push(shard);
      else candidates.set(rowId, [shard]);
    }
  }
  if (candidates.size !== wanted.size) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database "${this.databaseId}" row routing is unavailable or incomplete.`
    );
  }
  return candidates;
}

/** Resolve logical rows without loading unaddressed documents. */
export function resolveLocalRows(
  this: DatabaseSourceRuntime,
  rowIds: readonly string[],
  reason: DatabaseRowShardDocLoadReason = 'write'
): ReadonlyMap<
  string,
  { shard: DatabaseShardManifestEntry; doc: DatabaseShardDocument }
> {
  const candidates = findLocalRowShards.call(this, rowIds);
  const wanted = new Set(rowIds);
  const docs = new Map<
    string,
    { doc: DatabaseShardDocument; order: Set<string> }
  >();
  const resolved = new Map<
    string,
    { shard: DatabaseShardManifestEntry; doc: DatabaseShardDocument }
  >();
  const load = (shard: DatabaseShardManifestEntry) => {
    let loaded = docs.get(shard.serverDocId);
    if (!loaded) {
      const doc = this.ensureShardObserved(shard, reason);
      if (!this.hasCompleteLoadedShardRows(shard, doc))
        throw new BlockSuiteError(
          ErrorCode.DatabaseBlockError,
          `Database "${this.databaseId}" row shard is not fully loaded.`
        );
      loaded = {
        doc,
        order: new Set(
          doc.spaceDoc
            .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
            .toArray()
            .map(String)
        ),
      };
      docs.set(shard.serverDocId, loaded);
    }
    return loaded;
  };
  for (const rowId of wanted) {
    const holders = candidates.get(rowId)!.filter(shard => {
      const { doc, order } = load(shard);
      return (
        order.has(rowId) &&
        isReadonlyYMap(
          doc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY).get(rowId)
        )
      );
    });
    if (holders.length > 1)
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row "${rowId}" has ambiguous shard routing.`
      );
    const shard = holders[0];
    if (!shard)
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row "${rowId}" routing disagrees with its loaded document.`
      );
    resolved.set(rowId, { shard, doc: load(shard).doc });
  }
  return resolved;
}

export function currentWriterId(this: DatabaseSourceRuntime): string {
  return (
    this._model.store.getOptional(WriterInfoProvider)?.getWriterInfo()?.id ?? ''
  );
}

export function writeShardedCellToLocalDoc(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown
): void {
  void writePreparedShardedCellsToLocalDocs.call(
    this,
    new Map([[rowId, { [propertyId]: { columnId: propertyId, value } }]])
  );
}

export function resolveShardedCellForWrite(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown,
  actor: DataSourceWriteActor
): RowShardCell | null {
  this.assertFileIndexCellMutationAllowed(propertyId, actor);
  const type = this.propertyTypeGet(propertyId);
  if (type == null) {
    return null;
  }
  const propertyData = this.propertyDataGet(propertyId);
  const newValue = this.normalizeSchemaLockedSelectValue(
    type,
    propertyData,
    value
  );
  if (newValue === SCHEMA_LOCKED_INVALID_CELL_VALUE) {
    return null;
  }
  const resolvedValue = this.resolveRawValueForProperty(
    rowId,
    propertyId,
    type,
    propertyData,
    newValue
  );
  const persistedValue = this.serializeRawValueForProperty(
    type,
    propertyData,
    resolvedValue
  );
  assertDatabaseCellWithinTypeLimit({
    source: 'Database editor',
    columnType: type,
    rowId,
    columnId: propertyId,
    value: persistedValue,
  });
  return { columnId: propertyId, value: persistedValue };
}

export function writeShardedColumnCellsToLocalDocs(
  this: DatabaseSourceRuntime,
  propertyId: string,
  cells: Record<string, unknown>,
  rootMutation?: DatabaseRowMutation['rootMutation']
): void {
  void writePreparedShardedCellsToLocalDocs.call(
    this,
    new Map(
      Object.entries(cells).map(([rowId, value]) => [
        rowId,
        { [propertyId]: { columnId: propertyId, value } },
      ])
    ),
    rootMutation
  );
}

/** Policy and serialization happen before the common addressed write kernel. */
export function writeShardedRowCellsToLocalDocs(
  this: DatabaseSourceRuntime,
  rowIds: readonly string[],
  values: Readonly<Record<string, unknown>>
): void {
  const prepared = new Map<string, RowShardCells>();
  for (const rowId of rowIds) {
    const cells: RowShardCells = {};
    for (const [id, value] of Object.entries(values)) {
      const cell = this.resolveShardedCellForWrite(rowId, id, value, 'user');
      if (!cell)
        throw new BlockSuiteError(
          ErrorCode.DatabaseBlockError,
          'The column value cannot be written.'
        );
      cells[id] = cell;
    }
    prepared.set(rowId, cells);
  }
  void writePreparedShardedCellsToLocalDocs.call(this, prepared);
}

export function writePreparedShardedCellsToLocalDocs(
  this: DatabaseSourceRuntime,
  prepared: ReadonlyMap<string, RowShardCells>,
  rootMutation?: DatabaseRowMutation['rootMutation']
): Promise<LocalPublicationReceipt | null> {
  if (!rootMutation && prepared.size === 1) {
    const [rowId, cells] = [...prepared][0]!;
    const pending = pendingCreatedRowsOf(this.storageDoc)?.get(rowId);
    if (pending) {
      if (pending.accepting) {
        Object.assign(pending.cells, structuredClone(cells));
        // Readers of the pending row see the cell at once.
        this._shardedRowsRevision.value += 1;
        return pending.receipt;
      }
      // The row is already on its way; this edit follows its receipt and
      // reads back from here until then. Keystrokes before the follow-up
      // starts join it, so it sends their latest value once.
      let followup = pending.followup;
      if (followup && !followup.started) {
        Object.assign(followup.cells, structuredClone(cells));
      } else {
        const next: PendingRowFollowup = {
          cells: structuredClone(cells),
          started: false,
          receipt: pending.receipt.then(() => {
            next.started = true;
            if (this._disposed) throw new PublicationError('CANCELLED');
            return writePreparedShardedCellsToLocalDocs.call(
              this,
              new Map([[rowId, next.cells]])
            );
          }),
        };
        pending.followup = followup = next;
        publicationDocIds.set(
          next.receipt,
          publicationDocIds.get(pending.receipt) ?? []
        );
        trackRowPublication(this, next.receipt);
      }
      recordPendingRowUpdate(
        this.storageDoc,
        rowId,
        cells,
        followup.receipt,
        () => {
          if (!this._disposed) this._shardedRowsRevision.value += 1;
        }
      );
      this._shardedRowsRevision.value += 1;
      return followup.receipt;
    }
  }
  const addressed = this.resolveLocalRows([...prepared.keys()], 'row-window');
  const removable = removableColumnIds(this);
  const lineageColumns = databaseLineageColumns(this._model.props.columns);
  if (!rootMutation && usesRowEditPort(this)) {
    const changes = new Map<
      DatabaseShardManifestEntry,
      { doc: DatabaseShardDocument; edits: DatabaseRowEdit[] }
    >();
    const shownUpdates = new Map<string, RowShardCells>();
    for (const [rowId, { shard, doc }] of addressed) {
      let change = [...changes].find(
        ([entry]) => entry.shardId === shard.shardId
      )?.[1];
      if (!change) {
        change = { doc, edits: [] };
        changes.set(shard, change);
      }
      const raw = doc.spaceDoc
        .getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)
        .get(rowId);
      if (!isReadonlyYMap(raw))
        throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      // Preserve no-op intent across a later worker basis: do not overwrite a
      // peer's value when the user left this field unchanged in the view. A
      // field with an edit still on its way is always sent: typing back to the
      // shard value is a change, and so is the edit that follows a receipt.
      const inFlight = readPendingRowUpdateCells(this.storageDoc, rowId);
      const stored: RowShardCells = {};
      raw.forEach((value, id) => {
        stored[id] = normalizeRowShardCell(id, value);
      });
      const shown = shownRowCells(lineageColumns, stored);
      const changed = Object.fromEntries(
        Object.entries(prepared.get(rowId)!).filter(
          ([id, cell]) =>
            (inFlight !== undefined && id in inFlight) ||
            JSON.stringify(
              shown[id] ?? normalizeRowShardCell(id, undefined)
            ) !== JSON.stringify(cell)
        )
      );
      if (Object.keys(changed).length) shownUpdates.set(rowId, changed);
      const cells: RowShardCells = {
        ...cellsKeptForPendingCopies(this, changed, stored),
        ...changed,
      };
      // The worker stores the row as this plan leaves it: the leftover cells
      // of deleted columns and earlier types leave, so they do not count.
      assertDatabaseRowWithinByteBudget({
        source: 'Database editor',
        rowId,
        cells: Object.entries(
          databaseRowCellsAfterWrite({
            lineageColumns,
            removable,
            stored,
            written: cells,
          })
        ).map(([id, cell]) => [id, cell.value] as const),
      });
      const system = (id: string) =>
        normalizeRowShardCell(id, raw.get(id)).value;
      change.edits.push({
        type: 'set-cells',
        rowId,
        cells: {
          ...cells,
          ...createDatabaseRowUpdateSystemFieldCells({
            now: Date.now(),
            userId: this.currentWriterId(),
            createdTimeValue: system(DATABASE_ROW_CREATED_TIME_FIELD_ID),
            createdByValue: system(DATABASE_ROW_CREATED_BY_FIELD_ID),
            updatedByValue: system(DATABASE_ROW_UPDATED_BY_FIELD_ID),
          }),
        },
      });
    }
    const committed = queueRowEdits(this, 'update', changes);
    showPendingRowUpdates(this, shownUpdates, committed);
    return committed;
  }
  const changes = new Map<DatabaseShardManifestEntry, LocalRowChange>();
  for (const [rowId, { shard, doc }] of addressed) {
    let change = [...changes].find(
      ([entry]) => entry.shardId === shard.shardId
    )?.[1];
    if (!change) {
      change = { doc, rows: loadedLocalRows(this, shard, doc) };
      changes.set(shard, change);
    }
    const row = change.rows.find(candidate => candidate.rowId === rowId)!;
    const cells = prepared.get(rowId)!;
    row.cells = databaseRowCellsAfterWrite({
      lineageColumns,
      // A combined definition and cell write keeps them: its definition
      // change may be the one that brings the column back.
      removable: rootMutation ? new Set() : removable,
      stored: row.cells,
      written: {
        ...cellsKeptForPendingCopies(this, cells, row.cells),
        ...cells,
      },
    });
    assertDatabaseRowWithinByteBudget({
      source: 'Database editor',
      rowId,
      cells: Object.entries(row.cells).map(
        ([id, cell]) => [id, cell.value] as const
      ),
    });
    Object.assign(
      row.cells,
      createDatabaseRowUpdateSystemFieldCells({
        now: Date.now(),
        userId: this.currentWriterId(),
        createdTimeValue: row.cells[DATABASE_ROW_CREATED_TIME_FIELD_ID]?.value,
        createdByValue: row.cells[DATABASE_ROW_CREATED_BY_FIELD_ID]?.value,
        updatedByValue: row.cells[DATABASE_ROW_UPDATED_BY_FIELD_ID]?.value,
      })
    );
  }
  const committed = applyLocalRowChanges(
    this,
    changes,
    undefined,
    rootMutation ? 'schema' : 'update',
    rootMutation
  );
  showPendingRowUpdates(this, prepared, committed);
  return committed;
}

/**
 * The writer answers asynchronously; until its receipt the edited cells read
 * back from here instead of the older shard value, so a field keeps every
 * keystroke while earlier writes land.
 */
function showPendingRowUpdates(
  context: DatabaseSourceRuntime,
  updates: ReadonlyMap<string, RowShardCells>,
  committed: Promise<LocalPublicationReceipt | null>
): void {
  const refresh = () => {
    if (!context._disposed) context._shardedRowsRevision.value += 1;
  };
  for (const [rowId, cells] of updates)
    recordPendingRowUpdate(
      context.storageDoc,
      rowId,
      cells,
      committed,
      refresh
    );
  if (updates.size) refresh();
}

export function deleteShardedRowsFromLocalDocs(
  this: DatabaseSourceRuntime,
  rowIds: string[]
): void {
  const plan = planRowSet(this, [], rowIds, 'user');
  if (plan) void publishRowSet(this, plan);
}

type LocalRow = { rowId: string; cells: RowShardCells };
type LocalRowChange = { doc: DatabaseShardDocument; rows: LocalRow[] };

/** The publication stage a staged root model belongs to. */
const stagedRootStages = new WeakMap<object, DatabasePublicationStage>();

/**
 * The stage whose parts hold the directory of `model`: the publication stage
 * of a staged root model, the creation stage of a database in creation.
 */
function modelDirectoryStage(
  context: DatabaseSourceRuntime,
  model: DatabaseBlockModel
): DatabasePublicationStage {
  const stage =
    stagedRootStages.get(model) ??
    getDatabaseCreationStage(context.storageDoc)?.stage;
  if (!stage) throw new PublicationError('FORMAT_MISMATCH');
  return stage;
}

/** Stores `manifest` as the directory of `model` in the parts of its stage. */
function writeModelDirectory(
  context: DatabaseSourceRuntime,
  model: DatabaseBlockModel,
  manifest: DatabaseShardManifest
): void {
  writeDatabaseRootDirectory(
    model.yBlock,
    publicationDirectoryPartMaps(modelDirectoryStage(context, model)),
    manifest
  );
}

/**
 * Stores `change` of the directory of `model` as a root mutation reads it:
 * the complete directory of its stage, including the shards the publication
 * wrote before the mutation.
 */
export function changeModelDirectory(
  context: DatabaseSourceRuntime,
  model: DatabaseBlockModel,
  change: (manifest: DatabaseShardManifest) => DatabaseShardManifest
): void {
  const stage = modelDirectoryStage(context, model);
  writeDatabaseRootDirectory(
    model.yBlock,
    publicationDirectoryPartMaps(stage),
    change(readPublicationDirectory(model.yBlock, publicationStageDocs(stage)))
  );
}

/**
 * The cells of a stored row as its readers see them, by column id: a column
 * whose type changed reads its cell through its lineage.
 */
function shownRowCells(
  lineageColumns: ReturnType<typeof databaseLineageColumns>,
  stored: RowShardCells
): RowShardCells {
  if (!lineageColumns.length) return stored;
  return projectDatabaseRowCells(
    lineageColumns,
    { ...stored },
    cell => cell.value,
    (columnId, value) => ({ columnId, value })
  );
}

/**
 * New cells by column id in the stored form of a new row, by cell key: a
 * column whose type changed stores its cells under its current key.
 */
function storedFormCells(
  lineageColumns: ReturnType<typeof databaseLineageColumns>,
  cells: RowShardCells
): RowShardCells {
  if (!lineageColumns.length) return cells;
  return databaseRowCellsAfterWrite({
    lineageColumns,
    removable: new Set(),
    stored: {},
    written: cells,
  });
}

/**
 * A column duplicated with its values waits for the server to fill it and
 * shows the source as it was when it was duplicated. Before a source cell
 * changes, its waiting copies therefore get the cell as it is now; an absent
 * source cell becomes an empty cell in the copy.
 */
function cellsKeptForPendingCopies(
  context: DatabaseSourceRuntime,
  changed: RowShardCells,
  stored: RowShardCells
): RowShardCells {
  const kept: RowShardCells = {};
  const columns = context._model.props.columns;
  for (const column of columns) {
    const source = column.valuesFrom;
    if (!source || !(source in changed)) continue;
    if (column.id in changed || stored[column.id]) continue;
    const sourceColumn = columns.find(candidate => candidate.id === source);
    // The copy keeps its type: a source whose type changed since still has
    // its earlier cells, and the copy reads them as its own type.
    const cell = sourceColumn
      ? readDatabaseCellAs(
          databaseLineageColumns([sourceColumn])[0] ?? sourceColumn,
          column,
          cellKey => stored[cellKey]?.value
        )
      : undefined;
    kept[column.id] = { columnId: column.id, value: cell?.value ?? null };
  }
  return kept;
}

/** Deleted columns and earlier cell keys whose leftover cells a written row drops. */
function removableColumnIds(
  context: DatabaseSourceRuntime
): ReadonlySet<string> {
  return removableDeletedColumnIds({
    deletedColumns: normalizeDatabaseDeletedColumns(
      context._model.props.deletedColumns
    ),
    definedColumnIds: databaseCurrentCellKeys(context._model.props.columns),
    now: Date.now(),
  });
}

/**
 * Rows a shard merge writes again drop the leftover cells of deleted columns;
 * a converted value whose earlier key leaves moves to its current key.
 */
function withoutRemovableCells(
  context: DatabaseSourceRuntime,
  rows: readonly LocalRow[]
): LocalRow[] {
  const removable = removableColumnIds(context);
  if (!removable.size) return [...rows];
  const lineageColumns = databaseLineageColumns(context._model.props.columns);
  return rows.map(row => ({
    rowId: row.rowId,
    cells: databaseRowCellsAfterWrite({
      lineageColumns,
      removable,
      stored: row.cells,
      written: {},
    }),
  }));
}

function loadedLocalRows(
  context: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  doc: DatabaseShardDocument
): LocalRow[] {
  if (!context.hasCompleteLoadedShardRows(shard, doc)) {
    // Sync barrier mark for scripts/benchmarks/database-import-metrics.mjs: ids and counters, no row contents.
    const name = `app-base-sync-barrier:${doc.id}:write-incomplete`;
    performance.clearMarks(name);
    performance.mark(name, {
      detail: {
        phase: 'write-incomplete',
        docId: doc.id,
        rootDocId: context.storageDoc.id,
        ready: doc.ready,
        rowOrder: doc.spaceDoc.getArray(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
          .length,
        rowsMap: doc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY).size,
        rowCount: shard.rowCount,
        loadedRows: context.readLoadedShardRows(shard).length,
      },
    });
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Database row shard is not completely loaded for writing.'
    );
  }
  const map = doc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  return canonicalDatabaseRowOrder(
    doc.spaceDoc.getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY).toArray()
  ).map(rowId => {
    const raw = map.get(rowId);
    if (!isReadonlyYMap(raw))
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row "${rowId}" is unavailable.`
      );
    const cells: RowShardCells = {};
    raw.forEach((cell, id) => {
      cells[id] = normalizeRowShardCell(id, cell);
    });
    return { rowId, cells };
  });
}

/** Preserve the existing Y.Maps and transaction origin, including local Undo. */
function reconcileLocalRows(
  doc: YDoc,
  rows: readonly LocalRow[],
  compute?: DatabaseComputePort
): void {
  if (compute) {
    reconcileDatabaseRowsWithKernel(compute, doc, rows);
    return;
  }
  const map = doc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  const order = doc.getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY);
  const wanted = new Set(rows.map(row => row.rowId));
  doc.transact(() => {
    for (const id of map.keys()) if (!wanted.has(id)) map.delete(id);
    for (const row of rows) {
      const previous = map.get(row.rowId);
      const raw = isReadonlyYMap(previous) ? previous : new YMap();
      if (raw !== previous) map.set(row.rowId, raw);
      raw.forEach((_value, id) => {
        if (!(id in row.cells)) raw.delete(id);
      });
      for (const [id, cell] of Object.entries(row.cells)) {
        const stored = toDatabaseRowStoredCell(id, cell);
        if (JSON.stringify(raw.get(id)) !== JSON.stringify(stored))
          raw.set(id, stored);
      }
    }
    const ids = rows.map(row => row.rowId);
    for (const splice of planDatabaseRowOrderSplices(order.toArray(), ids)) {
      if (splice.deleteCount) order.delete(splice.index, splice.deleteCount);
      if (splice.insert.length) order.insert(splice.index, splice.insert);
    }
  });
}

function usesRowEditPort(context: DatabaseSourceRuntime): boolean {
  return (
    !getDatabaseCreationStage(context.storageDoc) &&
    !context.isFileIndexDatabase() &&
    !context._model.props.columns.some(
      column => column.type === 'relation' && column.data?.inverseFieldId
    )
  );
}
function submitRowEdits(
  context: DatabaseSourceRuntime,
  kind: DatabaseRowMutation['kind'],
  rowEdits: NonNullable<DatabaseRowMutation['rowEdits']>,
  maintenance = false,
  signal?: AbortSignal
): Promise<LocalPublicationReceipt | null> {
  const immutable = new Map(
    [...rowEdits].map(([shard, change]) => [
      shard,
      { doc: change.doc, edits: structuredClone(change.edits) },
    ])
  );
  return context.commitRowMutation(
    {
      kind,
      changes: new Map(),
      rowEdits: immutable,
      interactive: true,
      maintenance,
    },
    signal
  );
}
function queueRowEdits(
  context: DatabaseSourceRuntime,
  kind: DatabaseRowMutation['kind'],
  rowEdits: NonNullable<DatabaseRowMutation['rowEdits']>
): Promise<LocalPublicationReceipt | null> {
  const committed = submitRowEdits(context, kind, rowEdits);
  trackRowPublication(context, committed);
  scheduleShardMerges(context, kind, rowEdits.keys(), committed);
  return committed;
}

/** Writes that can shrink a shard re-plan merges once they committed. */
function scheduleShardMerges(
  context: DatabaseSourceRuntime,
  kind: DatabaseRowMutation['kind'],
  shards: Iterable<DatabaseShardManifestEntry>,
  committed: Promise<LocalPublicationReceipt | null>
): void {
  if (kind !== 'delete' && kind !== 'update' && kind !== 'move') return;
  const changed = new Set([...shards].map(shard => shard.shardId));
  // The merge belongs to the write: a local flush waits for it, so the next
  // write is planned against the merged manifest. Publication scopes keep
  // the write's own receipt. A failed write publishes nothing to merge; its
  // tracked publication reports the error.
  trackRowMaintenance(
    context,
    committed.then(
      () => mergeSmallShards(context, changed),
      () => {}
    )
  );
}

/**
 * Each shard a committed write left below the merge threshold joins an
 * order-neighbour in one move publication (planDatabaseRowShardMerges); the
 * writer removes the emptied source from the manifest. File-index databases
 * keep their registry-assigned writer shards. A merge that cannot run now
 * leaves the valid unmerged layout, and the next write re-plans it.
 */
async function mergeSmallShards(
  context: DatabaseSourceRuntime,
  changed: ReadonlySet<string>
): Promise<void> {
  if (
    context._disposed ||
    context.isFileIndexDatabase() ||
    getDatabaseCreationStage(context.storageDoc)
  )
    return;
  const manifest = requireCompleteShardDirectory(
    context._model,
    context.databaseId
  );
  const merges: Promise<LocalPublicationReceipt | null>[] = [];
  if (usesRowEditPort(context)) {
    const docs = new Map<string, DatabaseShardDocument>();
    for (const merge of planDatabaseRowShardMergeEdits(
      manifest.shards,
      changed,
      manifest.targetBytes,
      (source, target) => {
        // Write leases release both documents once the merge is confirmed.
        const sourceDoc = context.ensureShardObserved(source, 'write');
        docs.set(source.shardId, sourceDoc);
        docs.set(target.shardId, context.ensureShardObserved(target, 'write'));
        // Transfers name every moving row; only the complete source document
        // lists them.
        return context.hasCompleteLoadedShardRows(source, sourceDoc)
          ? canonicalDatabaseRowOrder(
              sourceDoc.spaceDoc
                .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
                .toArray()
                .map(String)
            )
          : undefined;
      }
    ))
      merges.push(
        submitRowEdits(
          context,
          'move',
          new Map([
            [
              merge.source,
              {
                doc: docs.get(merge.source.shardId)!,
                edits: [...merge.sourceEdits],
              },
            ],
            [
              merge.target,
              {
                doc: docs.get(merge.target.shardId)!,
                edits: [...merge.targetEdits],
              },
            ],
          ]),
          true
        )
      );
  } else
    for (const merge of planDatabaseRowShardMerges(
      manifest.shards,
      changed,
      DATABASE_LARGE_DATA_POLICY.shard.mergeBelowBytes,
      manifest.targetBytes
    )) {
      const source = manifest.shards.find(
        shard => shard.shardId === merge.sourceShardId
      );
      const target = manifest.shards.find(
        shard => shard.shardId === merge.targetShardId
      );
      if (!source || !target) continue;
      // Write leases release both documents once the merge is confirmed.
      const sourceDoc = context.ensureShardObserved(source, 'write');
      const targetDoc = context.ensureShardObserved(target, 'write');
      if (
        !context.hasCompleteLoadedShardRows(source, sourceDoc) ||
        !context.hasCompleteLoadedShardRows(target, targetDoc)
      )
        continue;
      const moving = withoutRemovableCells(
        context,
        loadedLocalRows(context, source, sourceDoc)
      );
      const staying = loadedLocalRows(context, target, targetDoc);
      const merged = submitLocalRowChanges(
        context,
        new Map([
          [source, { doc: sourceDoc, rows: [] }],
          [
            target,
            {
              doc: targetDoc,
              rows:
                merge.position === 'prepend'
                  ? [...moving, ...staying]
                  : [...staying, ...moving],
            },
          ],
        ]),
        undefined,
        'move',
        undefined,
        true
      );
      merges.push(merged);
    }
  // A rejected merge (e.g. a concurrent writer) keeps the valid unmerged
  // layout; the next write to these shards plans it again.
  await Promise.allSettled(merges);
}

/** Queue semantic row plans; this function never mutates a live member. */
function submitLocalRowChanges(
  context: DatabaseSourceRuntime,
  changes: ReadonlyMap<DatabaseShardManifestEntry, LocalRowChange>,
  baseShards?: DatabaseShardManifestEntry[],
  kind: DatabaseRowMutation['kind'] = 'update',
  rootMutation?: DatabaseRowMutation['rootMutation'],
  maintenance = false,
  signal?: AbortSignal
): Promise<LocalPublicationReceipt | null> {
  const immutable = new Map(
    [...changes].map(([shard, change]) => [
      shard,
      {
        doc: change.doc,
        rows: structuredClone(change.rows),
        before: structuredClone(loadedLocalRows(context, shard, change.doc)),
      },
    ])
  );
  return context.commitRowMutation(
    {
      kind,
      changes: immutable,
      baseShards,
      rootMutation,
      interactive: true,
      maintenance,
    },
    signal
  );
}
function applyLocalRowChanges(
  context: DatabaseSourceRuntime,
  changes: ReadonlyMap<DatabaseShardManifestEntry, LocalRowChange>,
  baseShards?: DatabaseShardManifestEntry[],
  kind: DatabaseRowMutation['kind'] = 'update',
  rootMutation?: DatabaseRowMutation['rootMutation']
): Promise<LocalPublicationReceipt | null> {
  const committed = submitLocalRowChanges(
    context,
    changes,
    baseShards,
    kind,
    rootMutation
  );
  trackRowPublication(context, committed);
  scheduleShardMerges(context, kind, changes.keys(), committed);
  return committed;
}

type RootMutation = NonNullable<DatabaseRowMutation['rootMutation']>;
const rootChangeScopes = new WeakMap<
  object,
  {
    callbacks: RootMutation[];
    settled: Array<() => void>;
    error?: PublicationError;
  }
>();
const rootReadModels = new WeakMap<object, DatabaseBlockModel>();
export function getDatabaseRootReadModel(
  store: object
): DatabaseBlockModel | undefined {
  return rootReadModels.get(store);
}
export function assertNoAtomicRowMutation(
  context: DatabaseSourceRuntime
): void {
  const scope = rootChangeScopes.get(context.storageDoc);
  if (!scope) return;
  throw (scope.error = new PublicationError('FORMAT_MISMATCH'));
}
export function atomicDatabaseRootChange(
  context: DatabaseSourceRuntime,
  fn: () => void
): void {
  if (getDatabaseCreationStage(context.storageDoc)) {
    fn();
    return;
  }
  const existing = rootChangeScopes.get(context.storageDoc);
  if (existing) {
    fn();
    return;
  }
  const scope: {
    callbacks: RootMutation[];
    settled: Array<() => void>;
    error?: PublicationError;
  } = {
    callbacks: [],
    settled: [],
  };
  rootChangeScopes.set(context.storageDoc, scope);
  try {
    fn();
    if (scope.error) throw scope.error;
  } catch (error) {
    scope.callbacks.length = 0;
    scope.settled.forEach(settled => settled());
    throw error;
  } finally {
    rootChangeScopes.delete(context.storageDoc);
  }
  if (scope.callbacks.length)
    queueDatabaseRootMutation(
      context,
      stage => {
        for (const callback of scope.callbacks) callback(stage);
      },
      'schema',
      true,
      () => scope.settled.forEach(settled => settled())
    );
}

export function queueDatabaseRootMutation(
  context: DatabaseSourceRuntime,
  rootMutation: NonNullable<DatabaseRowMutation['rootMutation']>,
  kind: DatabaseRowMutation['kind'] = 'schema',
  interactive = true,
  onSettled?: () => void
): void {
  if (getDatabaseCreationStage(context.storageDoc)) {
    try {
      rootMutation(context._model);
    } finally {
      onSettled?.();
    }
    return;
  }
  const scope = rootChangeScopes.get(context.storageDoc);
  if (scope) {
    scope.callbacks.push(rootMutation);
    if (onSettled) scope.settled.push(onSettled);
    return;
  }
  const committed = context.commitRowMutation({
    kind,
    changes: new Map(),
    interactive,
    rootMutation: stage => {
      const prior = rootReadModels.get(context.storageDoc);
      rootReadModels.set(context.storageDoc, stage);
      try {
        rootMutation(stage);
      } finally {
        if (prior) rootReadModels.set(context.storageDoc, prior);
        else rootReadModels.delete(context.storageDoc);
      }
    },
  });
  if (onSettled) void committed.then(onSettled, onSettled);
  trackRowPublication(context, committed);
}

/** The helper facade cannot reach the live root or the live transaction API. */
function stagedRootModel(
  context: DatabaseSourceRuntime,
  doc: YDoc,
  block: YMap<unknown>,
  stage?: DatabasePublicationStage
): DatabaseBlockModel {
  const data = createYProxy<Record<string, unknown>>(block);
  const props = new Proxy(context._model.props, {
    get: (_, key) => {
      if (typeof key !== 'string')
        throw new PublicationError('FORMAT_MISMATCH');
      return data['prop:' + key];
    },
    set: (_, key, value) => {
      if (typeof key !== 'string')
        throw new PublicationError('FORMAT_MISMATCH');
      data['prop:' + key] = value;
      return true;
    },
  });
  const stageDoc = new Proxy(context.storageDoc.doc, {
    get: (target, key) =>
      key === 'spaceDoc' ? doc : Reflect.get(target, key, target),
  });
  const store = new Proxy(context.storageDoc, {
    get: (target, key) =>
      key === 'doc'
        ? stageDoc
        : key === 'transact'
          ? (callback: () => void) => doc.transact(callback)
          : key === 'captureSync'
            ? () => {}
            : Reflect.get(target, key, target),
  });
  const model = new Proxy(context._model, {
    get: (target, key) =>
      key === 'props'
        ? props
        : key === 'store'
          ? store
          : key === 'yBlock'
            ? block
            : Reflect.get(target, key, target),
  });
  if (stage) stagedRootStages.set(model, stage);
  return model;
}

const rowPublications = new WeakMap<object, Set<Promise<unknown>>>();
/** Per source: publications an open scope reports; a flush only waits. */
const scopeClaims = new WeakMap<object, WeakSet<Promise<unknown>>>();
/** Per source: publications waiting flushes report, by flush count. */
const flushClaims = new WeakMap<object, Map<Promise<unknown>, number>>();
/**
 * Rejections already told to a writer or the user. One failed write that
 * several publications or sources share (follow-up, group) is told once.
 */
const reportedRejections = new WeakSet<object>();
/** The row shard documents a publication writes. */
const publicationDocIds = new WeakMap<Promise<unknown>, readonly string[]>();
/** Publications whose writer awaits them; the writer receives a rejection. */
const writerClaims = new WeakSet<Promise<unknown>>();
/** Per storage doc: the sources freeing the documents a reported rejection held. */
const rejectionReportedHandlers = new WeakMap<object, Set<() => void>>();

function mutationDocIds(input: DatabaseRowMutation): string[] {
  return [...(input.rowEdits ?? input.changes).values()].map(
    change => change.doc.id
  );
}

function wasReported(error: unknown): boolean {
  return typeof error === 'object' && error !== null
    ? reportedRejections.has(error)
    : false;
}

function markReported(error: unknown): void {
  if (typeof error === 'object' && error !== null)
    reportedRejections.add(error);
}

function isClaimed(
  context: DatabaseSourceRuntime,
  publication: Promise<unknown>
): boolean {
  const key = context.storageDoc;
  return (
    writerClaims.has(publication) ||
    (scopeClaims.get(key)?.has(publication) ?? false) ||
    (flushClaims.get(key)?.get(publication) ?? 0) > 0
  );
}

/**
 * Hands a submitted publication to the writer that awaits it: its rejection
 * reaches that writer only, never a flush or a notice.
 */
export function claimRowPublication<T>(
  context: DatabaseSourceRuntime,
  publication: Promise<T>
): Promise<T> {
  writerClaims.add(publication);
  void publication.then(
    () => {},
    error => {
      markReported(error);
      consumeRejections(context, [publication]);
    }
  );
  return publication;
}

/**
 * Calls `handler` once a reported rejection no longer holds documents;
 * returns the unsubscribe.
 */
export function onRowPublicationRejectionReported(
  context: DatabaseSourceRuntime,
  handler: () => void
): () => void {
  let handlers = rejectionReportedHandlers.get(context.storageDoc);
  if (!handlers)
    rejectionReportedHandlers.set(context.storageDoc, (handlers = new Set()));
  handlers.add(handler);
  return () => handlers.delete(handler);
}

/** Removes reported rejections from the flush list and frees their documents. */
function consumeRejections(
  context: DatabaseSourceRuntime,
  publications: Iterable<Promise<unknown>>
): void {
  const pending = rowPublications.get(context.storageDoc);
  let consumed = false;
  for (const publication of publications)
    if (pending?.delete(publication)) consumed = true;
  if (consumed)
    for (const handler of rejectionReportedHandlers.get(context.storageDoc) ??
      [])
      handler();
}

/** A rejection without a waiting scope or flush reaches the user once. */
function reportUnclaimedRejection(
  context: DatabaseSourceRuntime,
  publication: Promise<unknown>,
  error: unknown
): void {
  if (
    !rowPublications.get(context.storageDoc)?.has(publication) ||
    isClaimed(context, publication)
  )
    return;
  if (!wasReported(error)) {
    // Without notifications the rejection stays for the next flush.
    if (!context.reportPublicationRejection(error)) return;
    markReported(error);
  }
  consumeRejections(context, [publication]);
}

const publicationScopes = new WeakMap<
  object,
  Set<Set<Promise<LocalPublicationReceipt | null>>>
>();

export function openPublicationScope(
  context: DatabaseSourceRuntime
): DatabasePublicationScope {
  const key = context.storageDoc;
  let scopes = publicationScopes.get(key);
  if (!scopes) publicationScopes.set(key, (scopes = new Set()));
  const commits = new Set<Promise<LocalPublicationReceipt | null>>();
  scopes.add(commits);
  let settled: Promise<readonly LocalPublicationReceipt[]> | undefined;
  return {
    settle(signal) {
      if (settled) return settled;
      // The scope covers commits submitted between open and settle; later
      // concurrent writers are not attributed to this owner.
      scopes!.delete(commits);
      if (!scopes!.size && publicationScopes.get(key) === scopes)
        publicationScopes.delete(key);
      const submitted = [...commits];
      // An owner that stops waiting hands its publications to a flush at once.
      const releaseClaims = () => {
        const claims = scopeClaims.get(key);
        for (const commit of submitted) claims?.delete(commit);
      };
      const collect = async () => {
        const results = await Promise.allSettled(submitted);
        releaseClaims();
        if (signal?.aborted) {
          // The owner stopped waiting: its rejections go to a flush or the
          // user instead of being dropped with it.
          results.forEach((result, index) => {
            if (result.status === 'rejected')
              reportUnclaimedRejection(
                context,
                submitted[index]!,
                result.reason
              );
          });
          throw new PublicationError('CANCELLED');
        }
        const receipts: LocalPublicationReceipt[] = [];
        const rejections: unknown[] = [];
        const rejected: Promise<unknown>[] = [];
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            // A write that changed nothing has no receipt to report.
            if (result.value) receipts.push(result.value);
            return;
          }
          rejected.push(submitted[index]!);
          if (wasReported(result.reason)) return;
          markReported(result.reason);
          rejections.push(result.reason);
        });
        // The scope owner receives every rejection; no flush reports them again.
        consumeRejections(context, rejected);
        if (rejections.length) throw combineRejections(rejections);
        return receipts;
      };
      const wait = new Promise<readonly LocalPublicationReceipt[]>(
        (resolve, reject) => {
          const abort = () => {
            releaseClaims();
            reject(new PublicationError('CANCELLED'));
          };
          if (signal?.aborted) {
            abort();
            void collect().catch(() => {});
            return;
          }
          signal?.addEventListener('abort', abort, { once: true });
          void collect()
            .then(resolve, reject)
            .finally(() => signal?.removeEventListener('abort', abort));
        }
      );
      settled = wait;
      return settled;
    },
  };
}
function pendingRowPublications(context: DatabaseSourceRuntime) {
  const key = context.storageDoc;
  let pending = rowPublications.get(key);
  if (!pending) rowPublications.set(key, (pending = new Set()));
  return pending;
}
export function trackRowPublication(
  context: DatabaseSourceRuntime,
  committed: Promise<LocalPublicationReceipt | null>
): void {
  const key = context.storageDoc;
  const pending = pendingRowPublications(context);
  pending.add(committed);
  const scopes = publicationScopes.get(key);
  if (scopes?.size) {
    for (const scope of scopes) scope.add(committed);
    let claims = scopeClaims.get(key);
    if (!claims) scopeClaims.set(key, (claims = new WeakSet()));
    claims.add(committed);
  }
  // A waiting scope or flush reports the failure; otherwise the user is told.
  void committed.then(
    () => pending.delete(committed),
    error => reportUnclaimedRejection(context, committed, error)
  );
}
/** Follow-up maintenance of a write that a local flush waits for. Scopes
 * report only the write's own receipt; a maintenance failure reaches a
 * waiting flush, otherwise the user. */
function trackRowMaintenance(
  context: DatabaseSourceRuntime,
  settled: Promise<void>
): void {
  const pending = pendingRowPublications(context);
  pending.add(settled);
  void settled.then(
    () => pending.delete(settled),
    error => reportUnclaimedRejection(context, settled, error)
  );
}
/**
 * Waits until every submitted local publication has settled and returns the
 * row shard documents of the rejections nobody reported yet; only they stay
 * held.
 */
/** Whether local publications of this source are still on their way. */
export function hasPendingRowPublications(
  context: DatabaseSourceRuntime
): boolean {
  return (rowPublications.get(context.storageDoc)?.size ?? 0) > 0;
}

export async function heldRowShardDocIds(
  context: DatabaseSourceRuntime
): Promise<ReadonlySet<string>> {
  const held = new Set<string>();
  const pending = rowPublications.get(context.storageDoc);
  if (!pending?.size) return held;
  const submitted = [...pending];
  const results = await Promise.allSettled(submitted);
  results.forEach((result, index) => {
    const publication = submitted[index]!;
    if (result.status === 'fulfilled' || !pending.has(publication)) return;
    for (const docId of publicationDocIds.get(publication) ?? [])
      held.add(docId);
  });
  return held;
}

/**
 * Waits for every submitted local publication. It throws the rejections it
 * reports itself (one as itself, several together) after all have settled;
 * a scope's own publications are only waited for.
 */
export function flushLocalRowPublications(
  context: DatabaseSourceRuntime
): Promise<void> | undefined {
  const key = context.storageDoc;
  const pending = rowPublications.get(key);
  if (!pending?.size) return;
  const submitted = [...pending];
  let flushing = flushClaims.get(key);
  if (!flushing) flushClaims.set(key, (flushing = new Map()));
  const claims = flushing;
  const own = new Set(
    submitted.filter(
      promise =>
        !writerClaims.has(promise) && !scopeClaims.get(key)?.has(promise)
    )
  );
  for (const promise of own)
    claims.set(promise, (claims.get(promise) ?? 0) + 1);
  return Promise.allSettled(submitted).then(results => {
    for (const promise of own) {
      const count = (claims.get(promise) ?? 1) - 1;
      if (count) claims.set(promise, count);
      else claims.delete(promise);
    }
    const rejections: unknown[] = [];
    const reported: Promise<unknown>[] = [];
    results.forEach((result, index) => {
      const promise = submitted[index]!;
      if (result.status === 'fulfilled' || !own.has(promise)) return;
      if (!pending.has(promise)) return;
      reported.push(promise);
      if (wasReported(result.reason)) return;
      markReported(result.reason);
      rejections.push(result.reason);
    });
    consumeRejections(context, reported);
    if (rejections.length) throw combineRejections(rejections);
  });
}

function openedBase(base: PublicationMemberBase) {
  const doc = new YDoc({ guid: base.docId });
  try {
    if (base.bin) applyUpdate(doc, base.bin);
  } catch {
    doc.destroy();
    throw new PublicationError('FORMAT_MISMATCH');
  }
  return { base, doc };
}
function rootBlock(
  doc: YDoc,
  databaseId: string,
  workspaceId: string
): YMap<unknown> {
  const blocks = doc.getMap('blocks');
  const systemRoot =
    classifyPageSubdocId(doc.guid, workspaceId)?.kind === 'database-root';
  const candidate = systemRoot
    ? blocks.get(databaseId)
    : pageBlockIdsOfKind(pageReadSource(doc, new Map()), 'affine:database')
        .map(id => ({ id, block: blocks.get(id) }))
        .find(
          ({ id, block }) =>
            block instanceof YMap &&
            (id === databaseId || block.get('prop:databaseId') === databaseId)
        )?.block;
  if (!(candidate instanceof YMap))
    throw new PublicationError('INCOMPLETE_LOCAL_BASE');
  return candidate;
}
/**
 * Directory of the database `databaseId` in the projected root `root`, its
 * part documents projected by `project` from their live state plus the
 * imported updates. Returns the directory and its part document ids.
 */
function readImportedDirectory(
  runtime: DatabaseSourceRuntime,
  root: YDoc,
  databaseId: string,
  project: (part: YDoc) => void
): { manifest: DatabaseShardManifest; partDocIds: string[] } {
  const block = rootBlock(root, databaseId, runtime._model.store.workspace.id);
  const partDocIds = readPublicationDirectoryPartDocIds(block);
  const parts = new Map<string, YDoc>();
  try {
    for (const partDocId of partDocIds) {
      const part = new YDoc({ guid: partDocId });
      parts.set(partDocId, part);
      const live = runtime._model.store.workspace.getDoc(partDocId);
      if (live) applyUpdate(part, encodeStateAsUpdate(live.spaceDoc));
      project(part);
    }
    return {
      manifest: readPublicationDirectory(block, id => parts.get(id)),
      partDocIds,
    };
  } finally {
    for (const part of parts.values()) part.destroy();
  }
}
/**
 * Directory of the database `databaseId` in the opened root `root`. Its head
 * names the part documents; they are read with the bases of `known` as one
 * basis and opened into `opened`. Returns the directory, its part document ids
 * and the bases read beyond `known`.
 */
async function readBaseDirectory(
  workspaceId: string,
  port: ReturnType<DatabaseSourceRuntime['getPublicationPort']>,
  root: YDoc,
  databaseId: string,
  known: readonly PublicationMemberBase[],
  opened: YDoc[],
  signal?: AbortSignal
): Promise<{
  manifest: DatabaseShardManifest;
  partIds: string[];
  bases: PublicationMemberBase[];
}> {
  let block: YMap<unknown>;
  try {
    block = rootBlock(root, databaseId, workspaceId);
  } catch {
    throw new PublicationError('FORMAT_MISMATCH');
  }
  const partIds = readPublicationDirectoryPartDocIds(block);
  const missing = partIds.filter(id => !known.some(base => base.docId === id));
  const bases = missing.length
    ? [...(await port.readPublicationBase(missing, signal))]
    : [];
  const docs = new Map<string, YDoc>();
  for (const id of partIds) {
    const base =
      known.find(member => member.docId === id) ??
      bases.find(member => member.docId === id);
    if (!base?.bin) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
    const part = openedBase(base);
    opened.push(part.doc);
    docs.set(id, part.doc);
  }
  return {
    manifest: readPublicationDirectory(block, id => docs.get(id)),
    partIds,
    bases,
  };
}
function semanticRows(doc: YDoc): LocalRow[] {
  return canonicalDatabaseRowOrder(
    doc.getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY).toArray()
  ).flatMap(rowId => {
    const row = doc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY).get(rowId);
    return row instanceof YMap
      ? [{ rowId, cells: structuredClone(row.toJSON()) as RowShardCells }]
      : [];
  });
}
async function planFileIndexMirror(
  context: DatabaseSourceRuntime,
  input: DatabaseRowMutation,
  manifest: DatabaseShardManifest,
  root: YDoc,
  initial: readonly PublicationMemberBase[],
  opened: YDoc[],
  signal?: AbortSignal
): Promise<{
  mutation: DatabaseRowMutation;
  bases: readonly PublicationMemberBase[];
}> {
  const bases = new Map(initial.map(base => [base.docId, base]));
  const mirror = input.mirror!;
  if (!context.isFileIndexDatabase())
    throw new PublicationError('FORMAT_MISMATCH');
  const port = context.getPublicationPort();
  const shardBases = await port.readPublicationBase(
    manifest.shards.map(shard => shard.serverDocId),
    signal
  );
  const before = new Map<string, LocalRow[]>();
  for (const shard of manifest.shards) {
    const base = shardBases.find(base => base.docId === shard.serverDocId);
    if (!base?.bin) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
    if (!bases.has(base.docId)) bases.set(base.docId, base);
    const stored = openedBase(base);
    opened.push(stored.doc);
    before.set(shard.shardId, semanticRows(stored.doc));
  }
  const rootModel = stagedRootModel(
    context,
    root,
    rootBlock(root, context.databaseId, context._model.store.workspace.id)
  );
  const desired: LocalRow[] = [];
  const previousModel = rootReadModels.get(context.storageDoc);
  rootReadModels.set(context.storageDoc, rootModel);
  try {
    for (const row of mirror.rows) {
      const cells: RowShardCells = {};
      for (const [propertyId, value] of Object.entries(row.values)) {
        const cell = context.resolveShardedCellForWrite(
          row.rowId,
          propertyId,
          value,
          'system'
        );
        if (!cell) throw new PublicationError('FORMAT_MISMATCH');
        cells[propertyId] = cell;
      }
      desired.push({ rowId: row.rowId, cells });
    }
  } finally {
    if (previousModel) rootReadModels.set(context.storageDoc, previousModel);
    else rootReadModels.delete(context.storageDoc);
  }
  const sourceKey = (row: LocalRow) =>
    JSON.stringify(row.cells[mirror.sourcePropertyId]?.value);
  const wanted = new Map(desired.map(row => [sourceKey(row), row]));
  const locations = new Map<string, LocalRow>();
  for (const rows of before.values())
    for (const row of rows) locations.set(row.rowId, row);
  for (const row of desired) {
    const occupied = locations.get(row.rowId);
    if (occupied && sourceKey(occupied) !== sourceKey(row))
      throw new PublicationError('LOCAL_CONFLICT');
  }
  const after = new Map<string, LocalRow[]>();
  const upserts: { rowId: string; created: boolean }[] = [];
  const deletedRowIds: string[] = [];
  const seen = new Set<string>();
  for (const shard of manifest.shards) {
    const rows: LocalRow[] = [];
    for (const row of before.get(shard.shardId)!) {
      const key = sourceKey(row);
      const current = wanted.get(key);
      if (!current && !seen.has(key) && mirror.retainedRowIds.has(row.rowId)) {
        seen.add(key);
        rows.push(row);
        continue;
      }
      if (!current || seen.has(key)) {
        deletedRowIds.push(row.rowId);
        continue;
      }
      seen.add(key);
      const cells = { ...row.cells, ...current.cells };
      const attachmentId = FILE_INDEX_COLUMNS.blobId;
      const previousAttachment = row.cells[attachmentId];
      if (
        previousAttachment &&
        JSON.stringify(normalizeAttachmentCell(previousAttachment.value)) ===
          JSON.stringify(
            normalizeAttachmentCell(current.cells[attachmentId]?.value)
          )
      )
        cells[attachmentId] = previousAttachment;
      rows.push({ rowId: row.rowId, cells });
      upserts.push({ rowId: row.rowId, created: false });
    }
    after.set(shard.shardId, rows);
  }
  const shards = [...manifest.shards];
  const pending = desired.filter(row => !seen.has(sourceKey(row)));
  if (pending.length) {
    const hostDoc = context._hostModel.store.doc.spaceDoc;
    let target = selectFileIndexWriterShard(
      shards,
      getFileIndexWriterShardPrefix(hostDoc),
      context._fileIndexWriterShardId,
      manifest.targetBytes
    );
    if (!target) {
      target = context.createLocalShardEntry(
        nextFileIndexWriterShardId(hostDoc, shards)
      );
      shards.push(target);
      before.set(target.shardId, []);
      after.set(target.shardId, []);
    }
    after.get(target.shardId)!.push(
      ...pending.map(row => ({
        rowId: row.rowId,
        cells: {
          ...createDatabaseRowCreationSystemFieldCells({
            now: Date.now(),
            userId: context.currentWriterId(),
          }),
          ...row.cells,
        },
      }))
    );
    upserts.push(...pending.map(row => ({ rowId: row.rowId, created: true })));
  }
  const changes = new Map<DatabaseShardManifestEntry, DatabaseLocalRowChange>();
  for (const shard of shards) {
    const prior = before.get(shard.shardId)!;
    const rows = after.get(shard.shardId)!;
    if (JSON.stringify(prior) === JSON.stringify(rows)) continue;
    const doc = context.ensureShardObserved(shard, 'write');
    if (!doc) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
    if (!bases.has(doc.id))
      bases.set(doc.id, { docId: doc.id, bin: null, clock: null, actor: null });
    changes.set(shard, { doc, rows, before: prior });
  }
  mirror.planned({ upserts, deletedRowIds });
  return {
    mutation: { ...input, changes, baseShards: shards },
    bases: Array.from(bases.values()),
  };
}

const publicationWriters = new WeakMap<
  object,
  { tail: Promise<unknown>; history: DatabaseMutationHistory }
>();

/** The row writer each source wrote through, and the database it wrote. */
const rowWriterUsers = new WeakMap<
  object,
  { port: DatabaseRowTransactionPort; databaseRootDocId: string }
>();

/** A closed database releases the documents its row writes kept open. */
export function releaseRowWriterSessions(context: DatabaseSourceRuntime) {
  const user = rowWriterUsers.get(context);
  if (!user) return;
  rowWriterUsers.delete(context);
  user.port.release(user.databaseRootDocId).catch(error => {
    console.error('[Database] Releasing kept writer documents failed', error);
  });
}

export function commitRowMutation(
  this: DatabaseSourceRuntime,
  input: DatabaseRowMutation,
  signal?: AbortSignal
): Promise<LocalPublicationReceipt | null> {
  if (input.changes.size || input.kind !== 'schema')
    assertNoAtomicRowMutation(this);
  const creation = getDatabaseCreationStage(this.storageDoc);
  if (
    input.rowEdits &&
    (creation ||
      input.changes.size ||
      input.createRows ||
      input.mirror ||
      input.restore ||
      input.rootMutation ||
      input.rootFields ||
      !usesRowEditPort(this))
  )
    throw new PublicationError('FORMAT_MISMATCH');
  if (creation) {
    if (signal?.aborted) throw new PublicationError('CANCELLED');
    // This append API requires a confirmed local basis and its own receipt.
    // Never silently omit an intent inside an in-flight provisioning group.
    if (input.createRows) throw new PublicationError('FORMAT_MISMATCH');
    const manifest = readPublicationDirectory(
      rootBlock(
        creation.stage.doc(this.storageDoc.id),
        this.databaseId,
        this._model.store.workspace.id
      ),
      publicationStageDocs(creation.stage)
    );
    const shards = [...(input.baseShards ?? manifest.shards)];
    for (const [entry, change] of input.changes) {
      const doc = creation.stage.doc(change.doc.id);
      if (doc !== change.doc.spaceDoc)
        throw new PublicationError('FORMAT_MISMATCH');
      const stored = semanticRows(doc).map(row => row.rowId);
      reconcileLocalRows(doc, change.rows);
      const rowIds = change.rows.map(row => row.rowId);
      const write: DatabaseShardRowWrite = {
        addedRowIds: databaseShardAddedRowIds(stored, rowIds),
        clocks: databaseShardDocWriteClocks(doc),
      };
      if (databaseShardRowWriteCondenseDue(entry, write))
        markDatabaseShardCondenseDue(doc);
      const updated = createDatabaseShardManifestEntry({
        ...entry,
        databaseId: this.databaseId,
        workspaceId: this.workspaceId,
        ...recordDatabaseShardRowWrite(entry, rowIds, write),
        rowCount: change.rows.length,
        firstRowId: change.rows[0]?.rowId,
        lastRowId: change.rows.at(-1)?.rowId,
        encodedBytes: encodeStateAsUpdate(doc).byteLength,
        valueKeys: summarizeDatabaseShardValueKeys(
          change.rows.map(row => row.cells),
          shardValueColumns(this)
        ),
      });
      const index = shards.findIndex(shard => shard.shardId === entry.shardId);
      if (index < 0) shards.push(updated);
      else shards[index] = updated;
    }
    writeDatabaseRootDirectory(
      this._model.yBlock,
      publicationDirectoryPartMaps(creation.stage),
      createDatabaseShardManifest({ ...manifest, shards })
    );
    input.rootMutation?.(this._model);
    return creation.receipt;
  }
  const writer = publicationWriter(this);
  const created: Array<[string, RowShardCells]> =
    input.kind === 'create'
      ? input.rowEdits
        ? [...input.rowEdits.values()].flatMap(change =>
            change.edits.flatMap(edit =>
              edit.type === 'insert'
                ? edit.rows.map((row): [string, RowShardCells] => [
                    row.rowId,
                    row.cells,
                  ])
                : []
            )
          )
        : [...input.changes.values()].flatMap(change => {
            const previous = new Set(change.before?.map(row => row.rowId));
            return change.rows
              .filter(row => !previous.has(row.rowId))
              .map((row): [string, RowShardCells] => [row.rowId, row.cells]);
          })
      : [];
  const existingPending = pendingCreatedRowsOf(this.storageDoc);
  const seenCreated = new Set<string>();
  for (const [rowId] of created) {
    if (existingPending?.has(rowId) || seenCreated.has(rowId))
      throw new PublicationError('LOCAL_CONFLICT');
    seenCreated.add(rowId);
  }
  const records: PendingCreatedRow[] = [];
  const ready = writer.tail.then(() => {
    for (const record of records) record.accepting = false;
    return commitRowMutationNow.call(this, input, signal);
  });
  publicationDocIds.set(ready, mutationDocIds(input));
  if (created.length) {
    const pending = ensurePendingCreatedRows(this.storageDoc);
    for (const [rowId, cells] of created) {
      const record = { cells, accepting: true, receipt: ready };
      records.push(record);
      pending.set(rowId, record);
    }
    const release = () => {
      for (const [rowId] of created)
        if (pending!.get(rowId)?.receipt === ready) pending!.delete(rowId);
    };
    void ready.then(release, release);
  }
  writer.tail = ready.then(
    () => {},
    () => {}
  );
  if (!input.maintenance) trackRowPublication(this, ready);
  return ready;
}

/** The row changes of one source as detached semantic data, and its open documents. */
function rowWriterChanges(
  context: DatabaseSourceRuntime,
  input: DatabaseRowMutation
): { changes: DatabaseRowChanges; live: Map<string, YDoc> } {
  const live = new Map<string, YDoc>([
    [context.storageDoc.id, context._model.store.doc.spaceDoc],
  ]);
  for (const change of (input.rowEdits ?? input.changes).values())
    live.set(change.doc.id, change.doc.spaceDoc);
  // The writer stores the directory in its part documents; those open here
  // receive its update and keep their client ids out of its actors.
  for (const partDocId of readDatabaseShardDirectoryPartDocIds(
    context._model
  )) {
    const part = context._model.store.workspace.getDoc(partDocId);
    if (part) live.set(partDocId, part.spaceDoc);
  }
  return {
    live,
    changes: {
      rootDocId: context.storageDoc.id,
      databaseId: context._model.id ?? context.databaseId,
      // The writer reads only the parts holding the written shards.
      directoryPartDocIds: readDatabaseShardDirectoryShardPartDocIds(
        context._model,
        [...(input.rowEdits ?? input.changes).keys()].map(
          shard => shard.serverDocId
        )
      ),
      changes: input.rowEdits
        ? [...input.rowEdits].map(([shard, change]) => ({
            shard,
            edits: change.edits,
          }))
        : [...input.changes].map(([shard, change]) => ({
            shard,
            before: change.before,
            rows: change.rows,
          })),
    },
  };
}

/** The stored updates of one source applied to its open documents. */
function rowWriterMembers(
  context: DatabaseSourceRuntime,
  live: ReadonlyMap<string, YDoc>,
  updates: readonly { docId: string; update: Uint8Array }[],
  written: Pick<DatabaseRowsWritten, 'shards'>,
  operationId: string
): DatabaseLivePublicationMember[] {
  return updates.map(({ docId, update }) => {
    const part = isDirectoryPartDoc(context, docId);
    let doc = live.get(docId);
    if (!doc) {
      const shard = written.shards.find(shard => shard.serverDocId === docId);
      const loaded = shard
        ? context.ensureShardObserved(shard, 'write')
        : part
          ? context.ensureWorkspaceDoc(docId)
          : undefined;
      doc = loaded?.spaceDoc;
    }
    if (!doc) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
    return {
      doc,
      update,
      origin: localPublicationApplyOrigin(operationId),
      directory: isDirectoryDoc(context, docId),
    };
  });
}

function isDirectoryPartDoc(
  context: DatabaseSourceRuntime,
  docId: string
): boolean {
  return (
    classifyPageSubdocId(docId, context._model.store.workspace.id)?.kind ===
    'database-directory-part'
  );
}

/** The root or a part: the documents that count the database's rows. */
function isDirectoryDoc(
  context: DatabaseSourceRuntime,
  docId: string
): boolean {
  return docId === context.storageDoc.id || isDirectoryPartDoc(context, docId);
}

/** After the row writer stored a source's changes: access binding and undo history. */
function finishRowWrite(
  context: DatabaseSourceRuntime,
  input: DatabaseRowMutation,
  written: DatabaseRowsWritten
): void {
  if (input.interactive !== false)
    publicationWriters
      .get(context.storageDoc)!
      .history.record(
        input.kind,
        [...written.historyChanges],
        input.maintenance
      );
}

/** One source of a row set group and its row set. */
export interface DatabaseRowSetGroupEntry {
  readonly source: DatabaseSourceRuntime;
  readonly set: DatabaseRowSet;
}

/** A planned row set group: its document count, committed as one publication. */
export interface DatabaseRowSetGroupPlan {
  /**
   * Documents the writer opens for the publication, before shard splits:
   * each root, the directory parts holding the shards written and each row
   * shard written. A write that reads more parts (no shard index in the
   * head, a column leaving every shard) fails in the writer with
   * LIMIT_EXCEEDED once it holds more than MAX_GROUP_DOCS documents.
   */
  readonly documents: number;
  /** One local publication, one receipt, all or nothing; null for an empty group. */
  commit(signal?: AbortSignal): Promise<LocalPublicationReceipt | null>;
}

/**
 * Plans the row sets of several sharded databases as one publication. Each
 * database appears once; only databases on the row edit port (no file
 * index, no two-way relation) take part. The group writes no undo history:
 * its databases change only together.
 */
export function planRowSetGroup(
  entries: readonly DatabaseRowSetGroupEntry[],
  actor: DataSourceWriteActor = 'user'
): DatabaseRowSetGroupPlan {
  if (
    new Set(entries.map(entry => entry.source.storageDoc)).size !==
      entries.length ||
    new Set(entries.map(entry => entry.source._model.store.workspace.id)).size >
      1
  )
    throw new PublicationError('FORMAT_MISMATCH');
  const planned: Array<{
    source: DatabaseSourceRuntime;
    rowEdits: ShardRowEdits;
    plan: RowSetPlan;
  }> = [];
  for (const { source, set } of entries) {
    if (!usesRowEditPort(source)) throw new PublicationError('FORMAT_MISMATCH');
    assertNoAtomicRowMutation(source);
    assertRowBatchWithinBudget(set.upserts.length + set.deleteRowIds.length);
    const plan = planRowSet(source, set.upserts, set.deleteRowIds, actor);
    if (!plan) continue;
    if (!('rowEdits' in plan)) throw new PublicationError('FORMAT_MISMATCH');
    planned.push({ source, rowEdits: plan.rowEdits, plan });
  }
  return {
    // The writer opens the root and the directory parts it is sent
    // (rowWriterChanges), plus the written row shards.
    documents: planned.reduce(
      (sum, entry) =>
        sum +
        1 +
        readDatabaseShardDirectoryShardPartDocIds(
          entry.source._model,
          [...entry.rowEdits.keys()].map(shard => shard.serverDocId)
        ).length +
        entry.rowEdits.size,
      0
    ),
    commit: signal => {
      if (!planned.length) return Promise.resolve(null);
      const writers = planned.map(entry => publicationWriter(entry.source));
      const ready = Promise.all(writers.map(writer => writer.tail)).then(() =>
        commitRowSetGroupNow(planned, signal)
      );
      for (const writer of writers)
        writer.tail = ready.then(
          () => {},
          () => {}
        );
      publicationDocIds.set(
        ready,
        planned.flatMap(({ rowEdits }) =>
          [...rowEdits.values()].map(change => change.doc.id)
        )
      );
      for (const { source, plan, rowEdits } of planned) {
        trackRowPublication(source, ready);
        // The caller of commit awaits the group and receives its rejection.
        void claimRowPublication(source, ready);
        scheduleShardMerges(
          source,
          plan.removesRows ? 'delete' : plan.kind,
          rowEdits.keys(),
          ready
        );
      }
      return ready;
    },
  };
}

async function commitRowSetGroupNow(
  planned: ReadonlyArray<{
    source: DatabaseSourceRuntime;
    rowEdits: ShardRowEdits;
    plan: RowSetPlan;
  }>,
  signal?: AbortSignal
): Promise<LocalPublicationReceipt | null> {
  if (signal?.aborted) throw new PublicationError('CANCELLED');
  const prepared = planned.map(({ source, rowEdits, plan }) => {
    const input: DatabaseRowMutation = {
      kind: plan.kind,
      changes: new Map(),
      rowEdits: new Map(
        [...rowEdits].map(([shard, change]) => [
          shard,
          { doc: change.doc, edits: structuredClone(change.edits) },
        ])
      ),
      interactive: false,
    };
    return { source, input, ...rowWriterChanges(source, input) };
  });
  const port = prepared[0]!.source.getRowWriterPort();
  for (const { source } of prepared)
    rowWriterUsers.set(source, {
      port,
      databaseRootDocId: source.storageDoc.id,
    });
  const result = await port.mutateGroup(
    {
      version: 1,
      operationId: crypto.randomUUID(),
      workspaceId: prepared[0]!.source._model.store.workspace.id,
      kind: prepared.some(entry => entry.input.kind === 'create')
        ? 'create'
        : prepared[0]!.input.kind,
      liveClientIds: prepared.flatMap(entry =>
        [...entry.live.values()].map(doc => doc.clientID)
      ),
      databases: prepared.map(entry => entry.changes),
    },
    signal
  );
  if (result.databases.length !== prepared.length)
    throw new PublicationError('FORMAT_MISMATCH');
  const members = prepared.flatMap((entry, index) => {
    const written = result.databases[index]!;
    const docIds = new Set([
      ...entry.live.keys(),
      ...written.shards.map(shard => shard.serverDocId),
    ]);
    return rowWriterMembers(
      entry.source,
      entry.live,
      result.updates.filter(update => docIds.has(update.docId)),
      written,
      result.receipt.operationId
    );
  });
  if (members.length !== result.updates.length)
    throw new PublicationError('INCOMPLETE_LOCAL_BASE');
  applyDatabaseLivePublication(
    members,
    () => {
      for (const { source } of prepared) source.refreshShardedRowsFromDocs();
    },
    () => {
      for (const { source } of prepared) bumpShardedRowsRevision(source);
    }
  );
  prepared.forEach((entry, index) =>
    finishRowWrite(entry.source, entry.input, result.databases[index]!)
  );
  return result.receipt;
}

async function commitRowMutationNow(
  this: DatabaseSourceRuntime,
  input: DatabaseRowMutation,
  signal?: AbortSignal
): Promise<LocalPublicationReceipt | null> {
  const context = this;
  if (signal?.aborted) throw new PublicationError('CANCELLED');
  // Row transactions cross the worker boundary as detached semantic data. Schema,
  // FileIndex mirror and semantic restore remain separately typed operations.
  if (
    (input.changes.size || input.rowEdits?.size) &&
    !input.createRows &&
    !input.mirror &&
    !input.restore &&
    !input.rootMutation &&
    !input.rootFields &&
    !context.isFileIndexDatabase() &&
    !context._model.props.columns.some(
      column => column.type === 'relation' && column.data?.inverseFieldId
    )
  ) {
    const { changes, live } = rowWriterChanges(context, input);
    const port = context.getRowWriterPort();
    rowWriterUsers.set(context, {
      port,
      databaseRootDocId: context.storageDoc.id,
    });
    const result = await port.mutate(
      {
        version: 1,
        operationId: crypto.randomUUID(),
        workspaceId: context._model.store.workspace.id,
        kind: input.kind,
        liveClientIds: [...live.values()].map(doc => doc.clientID),
        ...changes,
      },
      signal
    );
    applyDatabaseLivePublication(
      rowWriterMembers(
        context,
        live,
        result.updates,
        result,
        result.receipt.operationId
      ),
      () => context.refreshShardedRowsFromDocs(),
      () => bumpShardedRowsRevision(context)
    );
    finishRowWrite(context, input, result);
    return result.receipt;
  }
  const port = context.getPublicationPort();
  const rootId = context.storageDoc.id;
  const compute = databaseComputePort(context._model.store.workspace);
  // The live head names the directory parts, so that one read captures the
  // whole member set; parts the stored head adds are read after it.
  const sourceIds = [
    ...new Set([
      rootId,
      ...readDatabaseShardDirectoryPartDocIds(context._model),
      ...[...input.changes.values()].map(change => change.doc.id),
      ...(input.restore ?? []).map(change => change.docId),
    ]),
  ];
  let initial = await port.readPublicationBase(sourceIds, signal);
  const rootInput = initial.find(base => base.docId === rootId);
  if (!rootInput?.bin) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
  const rootBase = openedBase(rootInput);
  const opened: YDoc[] = [rootBase.doc];
  let stage: DatabasePublicationStage | undefined;
  let earlySemantics:
    | Map<string, ReturnType<typeof snapshotDatabaseSemantics>>
    | undefined;
  try {
    const source = await readBaseDirectory(
      context._model.store.workspace.id,
      port,
      rootBase.doc,
      context._model.id ?? context.databaseId,
      initial,
      opened,
      signal
    );
    initial = [...initial, ...source.bases];
    const manifest = source.manifest;
    if (input.createRows) {
      const createRows = input.createRows;
      if (
        input.kind !== 'create' ||
        createRows.position !== 'end' ||
        input.changes.size ||
        input.mirror ||
        input.restore ||
        input.baseShards ||
        input.rootFields ||
        context.isFileIndexDatabase()
      )
        throw new PublicationError('FORMAT_MISMATCH');
      if (context.readonly$.value) throw new PublicationError('FORBIDDEN');
      context.assertFileIndexRowMutationAllowed(
        createRows.actor,
        'Row creation'
      );
      const target =
        manifest.shards.at(-1) ??
        context.createLocalShardEntry(INITIAL_ROW_SHARD_ID);
      const appendBase = await port.readPublicationBase(
        [target.serverDocId],
        signal
      );
      const base = appendBase.find(
        member => member.docId === target.serverDocId
      );
      if (!base || (manifest.shards.length && !base.bin))
        throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      initial = [...initial, base];
      const liveIds = [context._model.store.doc.spaceDoc.clientID];
      const existing = context.getExistingShardDoc(target);
      if (existing) liveIds.push(existing.spaceDoc.clientID);
      stage = new DatabasePublicationStage(initial, liveIds);
      earlySemantics = new Map(
        stage
          .ids()
          .map(id => [
            id,
            measureDatabaseCompute(compute, 'history-before', 0, () =>
              snapshotDatabaseSemantics(stage!.doc(id))
            ),
          ])
      );
      const stagedRoot = stage.doc(rootId);
      const model = stagedRootModel(
        context,
        stagedRoot,
        rootBlock(
          stagedRoot,
          context._model.id ?? context.databaseId,
          context._model.store.workspace.id
        ),
        stage
      );
      const priorModel = rootReadModels.get(context.storageDoc);
      rootReadModels.set(context.storageDoc, model);
      let inserted: LocalRow[];
      // Shards whose fingerprints may hold a requested row; their documents
      // decide, so a false positive costs one read and never a conflict.
      let suspects: DatabaseShardManifestEntry[];
      try {
        input.rootMutation?.(model);
        const rows = createRows.values(model);
        if (
          !rows.length ||
          rows.length > DATABASE_LARGE_DATA_POLICY.jobs.bulkRowLimit
        )
          throw new PublicationError('LIMIT_EXCEEDED');
        const requested = new Set<string>();
        inserted = rows.map(row => {
          if (!row.rowId || requested.has(row.rowId))
            throw new PublicationError('LOCAL_CONFLICT');
          requested.add(row.rowId);
          const cells: RowShardCells =
            createDatabaseRowCreationSystemFieldCells({
              now: Date.now(),
              userId: context.currentWriterId(),
            });
          for (const [id, value] of Object.entries(row.values)) {
            // This atomic append API handles local fields. Inverse-bearing
            // relation edits use the existing relation planner API instead.
            const property = model.props.columns.find(
              column => column.id === id
            );
            if (property?.type === 'relation')
              throw new PublicationError('FORMAT_MISMATCH');
            const cell = context.resolveShardedCellForWrite(
              row.rowId,
              id,
              value,
              createRows.actor
            );
            if (!cell) throw new PublicationError('FORMAT_MISMATCH');
            cells[id] = cell;
          }
          return {
            rowId: row.rowId,
            cells: storedFormCells(
              databaseLineageColumns(model.props.columns),
              cells
            ),
          };
        });
        suspects = selectDatabaseShardsMayContainRows(
          manifest.shards,
          requested
        );
      } finally {
        if (priorModel) rootReadModels.set(context.storageDoc, priorModel);
        else rootReadModels.delete(context.storageDoc);
      }
      const before = semanticRows(stage.doc(target.serverDocId));
      if (before.length !== target.rowCount)
        throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      const requestedIds = new Set(inserted.map(row => row.rowId));
      const suspectIds = suspects
        .map(shard => shard.serverDocId)
        .filter(id => id !== target.serverDocId);
      const suspectBases = suspectIds.length
        ? await port.readPublicationBase(suspectIds, signal)
        : [];
      for (const base of suspectBases) {
        if (!base.bin) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
        const suspect = openedBase(base);
        opened.push(suspect.doc);
        if (
          suspect.doc
            .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
            .toArray()
            .some(id => requestedIds.has(String(id)))
        )
          throw new PublicationError('LOCAL_CONFLICT');
      }
      if (before.some(row => requestedIds.has(row.rowId)))
        throw new PublicationError('LOCAL_CONFLICT');
      input = {
        ...input,
        changes: new Map([
          [
            target,
            {
              doc: {
                id: target.serverDocId,
                spaceDoc: stage.doc(target.serverDocId),
              },
              before,
              rows: [...before, ...inserted],
            },
          ],
        ]),
        baseShards: manifest.shards.length ? manifest.shards : [target],
      };
    }
    if (input.mirror) {
      const mirrorPlan = await planFileIndexMirror(
        context,
        input,
        manifest,
        rootBase.doc,
        initial,
        opened,
        signal
      );
      input = mirrorPlan.mutation;
      initial = mirrorPlan.bases;
    }
    // Queued row intents may have captured the directory before an earlier
    // publication landed. Keep its current fingerprints and clocks; only new
    // shard addresses come from the captured insertion plan.
    const shards = input.mirror
      ? (input.baseShards ?? manifest.shards)
      : [
          ...manifest.shards,
          ...(input.baseShards ?? []).filter(
            entry =>
              !manifest.shards.some(stored => stored.shardId === entry.shardId)
          ),
        ];
    let next = nextDatabaseShardIndex({
      nextShardIndex: manifest.nextShardIndex,
      shards,
    });
    const planned: Array<{
      shard: DatabaseShardManifestEntry;
      doc?: DatabaseLocalRowChange['doc'];
      rows: LocalRow[];
    }> = [];
    const projected: DatabaseShardManifestEntry[] = [];
    const target = manifest.targetBytes;
    const hard = context.isFileIndexDatabase()
      ? DOCUMENT_PRODUCT_HARD_LIMIT_BYTES
      : manifest.hardLimitBytes;
    const changesById = new Map(
      [...input.changes].map(([shard, change]) => [shard.shardId, change])
    );
    for (const shard of shards) {
      const change = changesById.get(shard.shardId);
      if (!change) {
        projected.push(shard);
        continue;
      }
      const measure = (rows: readonly LocalRow[]) => {
        if (compute) return measureDatabaseRowsWithKernel(compute, rows);
        const sizing = new YDoc();
        try {
          reconcileLocalRows(sizing, rows);
          return encodeStateAsUpdate(sizing).byteLength;
        } finally {
          sizing.destroy();
        }
      };
      const groups = measureDatabaseCompute(
        compute,
        'partition',
        change.rows.length,
        () =>
          partitionDatabaseRowShardRows(change.rows, target, hard, measure, {
            priorCapacity: Math.max(1, shard.rowCount),
            balanceTail: !context.isFileIndexDatabase(),
          })
      );
      for (let i = 0; i < groups.length; i++) {
        const entry =
          i === 0
            ? shard
            : context.createLocalShardEntry(
                context.isFileIndexDatabase()
                  ? nextFileIndexWriterShardId(rootBase.doc, [
                      ...projected,
                      ...shards,
                    ])
                  : databaseRowShardId(++next)
              );
        const rows = groups[i]!;
        const staged = new YDoc();
        try {
          if (i === 0) {
            const base = initial.find(base => base.docId === change.doc.id);
            if (base?.bin) applyUpdate(staged, base.bin);
          }
          reconcileLocalRows(staged, rows, compute);
          const updated = context.updateLocalShardManifestEntry(
            entry,
            rows.map(row => row.rowId),
            encodeStateAsUpdate(staged).byteLength,
            summarizeDatabaseShardValueKeys(
              rows.map(row => row.cells),
              shardValueColumns(context)
            )
          );
          if (!context.isFileIndexDatabase() || rows.length)
            projected.push(updated);
          planned.push({
            shard: updated,
            doc: i === 0 && !input.createRows ? change.doc : undefined,
            rows,
          });
        } finally {
          staged.destroy();
        }
      }
    }
    // The admitted cost includes newly split targets plus the root, before
    // any workspace allocation or live mutation. Preserve the existing ceiling.
    const documentCount =
      1 + new Set(planned.map(plan => plan.shard.serverDocId)).size;
    if (documentCount > 64) throw new PublicationError('LIMIT_EXCEEDED');
    const plannedBytes = planned.reduce(
      (sum, plan) => sum + plan.shard.encodedBytes,
      0
    );
    const byteLimit = 4 * DOCUMENT_PRODUCT_HARD_LIMIT_BYTES;
    if (plannedBytes > byteLimit) throw new PublicationError('LIMIT_EXCEEDED');
    const rootManifest = createDatabaseShardManifest({
      ...manifest,
      shards: projected,
    });
    context.assertFileIndexRootSchemaWithinLimits({
      manifest: rootManifest,
      registryShards: projected,
    });

    const relationEdits: Array<{
      rowId: string;
      propertyId: string;
      before: string[];
      after: string[];
    }> = [];
    const columnsValue = rootBlock(
      rootBase.doc,
      context._model.id ?? context.databaseId,
      context._model.store.workspace.id
    ).get('prop:columns');
    const columns =
      columnsValue instanceof YArray ? columnsValue.toJSON() : columnsValue;
    const relationColumns = Array.isArray(columns)
      ? columns.filter(
          column => column?.type === 'relation' && column.data?.inverseFieldId
        )
      : [];
    for (const [, change] of input.changes) {
      const base = initial.find(base => base.docId === change.doc.id);
      if (!base?.bin && input.kind !== 'create' && !input.mirror)
        throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      // Basis validation remains mandatory; inverse planning is unnecessary
      // without inverse relation columns. Avoid decoding/cloning whole shards.
      if (!relationColumns.length) continue;
      const prior = openedBase(base!);
      opened.push(prior.doc);
      const before = semanticRows(prior.doc);
      const after = applyDatabaseRowIntent(
        change.before ?? before,
        change.rows,
        before
      );
      const old = new Map(before.map(row => [row.rowId, row]));
      const wanted = new Map(after.map(row => [row.rowId, row]));
      for (const id of new Set([...old.keys(), ...wanted.keys()]))
        for (const column of relationColumns) {
          const previous = old.get(id)?.cells[column.id]?.value;
          const nextValue = wanted.get(id)?.cells[column.id]?.value;
          const beforeRefs = Array.isArray(previous)
            ? previous.map(String)
            : [];
          const afterRefs = Array.isArray(nextValue)
            ? nextValue.map(String)
            : [];
          if (JSON.stringify(beforeRefs) !== JSON.stringify(afterRefs))
            relationEdits.push({
              rowId: id,
              propertyId: column.id,
              before: beforeRefs,
              after: afterRefs,
            });
        }
    }
    let inverse: readonly DatabaseRelationInverseEdit[] = [];
    if (relationEdits.length) {
      const planner = context.getRelationInversePlanner();
      if (!planner) throw new PublicationError('FORMAT_MISMATCH');
      inverse = await planner.plan(
        {
          source: { docId: rootId, databaseId: context.databaseId },
          edits: relationEdits,
        },
        signal
      );
    }
    const targetIds = [...new Set(inverse.map(edit => edit.target.docId))];
    const targetBases = targetIds.length
      ? [...(await port.readPublicationBase(targetIds, signal))]
      : [];
    const inverseCandidates: Array<{
      edit: DatabaseRelationInverseEdit;
      manifest: DatabaseShardManifest;
      partIds: string[];
      shards: DatabaseShardManifestEntry[];
    }> = [];
    for (const edit of inverse) {
      const base = targetBases.find(base => base.docId === edit.target.docId);
      if (!base?.bin) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      const openedTarget = openedBase(base);
      opened.push(openedTarget.doc);
      const target = await readBaseDirectory(
        context._model.store.workspace.id,
        port,
        openedTarget.doc,
        edit.target.databaseId,
        [...initial, ...targetBases],
        opened,
        signal
      );
      targetBases.push(...target.bases);
      inverseCandidates.push({
        edit,
        manifest: target.manifest,
        partIds: target.partIds,
        shards: selectDatabaseShardsMayContainRow(
          target.manifest.shards,
          edit.rowId
        ),
      });
    }
    // Fingerprints only narrow the shards; their documents, read together,
    // decide which one holds the row.
    const candidateIds = [
      ...new Set(
        inverseCandidates.flatMap(candidate =>
          candidate.shards.map(shard => shard.serverDocId)
        )
      ),
    ];
    const candidateOrders = new Map(
      (candidateIds.length
        ? await port.readPublicationBase(candidateIds, signal)
        : []
      ).map(base => {
        if (!base.bin) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
        const candidate = openedBase(base);
        opened.push(candidate.doc);
        return [
          base.docId,
          new Set(
            candidate.doc
              .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
              .toArray()
              .map(String)
          ),
        ] as const;
      })
    );
    const inversePlans = inverseCandidates.flatMap(
      ({ edit, manifest: targetManifest, partIds, shards }) => {
        const holders = shards.filter(shard =>
          candidateOrders.get(shard.serverDocId)?.has(edit.rowId)
        );
        if (holders.length > 1) throw new PublicationError('FORMAT_MISMATCH');
        return holders.map(shard => ({
          edit,
          shard,
          manifest: targetManifest,
          partIds,
        }));
      }
    );
    const allIds = [
      ...new Set([
        rootId,
        ...source.partIds,
        ...planned.map(plan => plan.shard.serverDocId),
        ...inversePlans.flatMap(plan => [
          plan.edit.target.docId,
          ...plan.partIds,
          plan.shard.serverDocId,
        ]),
        ...(input.restore ?? []).map(change => change.docId),
      ]),
    ];
    if (allIds.length > 64) throw new PublicationError('LIMIT_EXCEEDED');
    // Same-member cell updates already have one coherent storage capture.
    // The atomic publication commit rechecks every clock/actor, including races
    // during planning. Reload only when planning can expand/change that basis.
    const reuseInitial =
      input.kind === 'update' &&
      !input.createRows &&
      !input.mirror &&
      !input.restore &&
      !input.rootMutation &&
      !input.rootFields &&
      relationEdits.length === 0 &&
      allIds.length === initial.length &&
      allIds.every(id => initial.some(base => base.docId === id));
    const bases = reuseInitial
      ? initial
      : await port.readPublicationBase(allIds, signal);
    for (const prior of [...initial, ...targetBases]) {
      const current = bases.find(base => base.docId === prior.docId);
      if (
        current &&
        (current.clock?.getTime() !== prior.clock?.getTime() ||
          JSON.stringify(current.actor) !== JSON.stringify(prior.actor))
      )
        throw new PublicationError('LOCAL_CONFLICT');
    }
    const live = new Map<string, YDoc>([
      [rootId, context._model.store.doc.spaceDoc],
    ]);
    for (const plan of planned) {
      plan.doc ??= context.ensureShardObserved(plan.shard, 'write');
      if (!plan.doc) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      live.set(plan.shard.serverDocId, plan.doc.spaceDoc);
    }
    for (const id of allIds) {
      if (live.has(id)) continue;
      const doc = context.ensureWorkspaceDoc(id);
      if (doc) live.set(id, doc.spaceDoc);
    }
    if (!stage) {
      stage = new DatabasePublicationStage(
        bases,
        [...live.values()].map(doc => doc.clientID)
      );
    } else {
      for (const base of bases) {
        if (!stage.ids().includes(base.docId)) {
          if (base.bin || base.clock || base.actor)
            throw new PublicationError('LOCAL_CONFLICT');
          stage.addEmptyDoc(base.docId);
        }
        if (stage.doc(base.docId).clientID === live.get(base.docId)?.clientID)
          throw new PublicationError('FORMAT_MISMATCH');
      }
    }
    const semanticsBefore: Map<
      string,
      ReturnType<typeof snapshotDatabaseSemantics>
    > = earlySemantics ?? new Map();
    for (const id of allIds)
      if (!semanticsBefore.has(id))
        semanticsBefore.set(
          id,
          measureDatabaseCompute(compute, 'history-before', 0, () =>
            snapshotDatabaseSemantics(stage!.doc(id))
          )
        );
    for (const plan of planned) {
      const doc = stage.doc(plan.shard.serverDocId);
      const change = [...input.changes.values()].find(
        change => change.doc.id === plan.shard.serverDocId
      );
      const stored = semanticRows(doc);
      const rows = change
        ? applyDatabaseRowIntent(change.before ?? stored, plan.rows, stored)
        : plan.rows;
      reconcileLocalRows(doc, rows, compute);
      const rowIds = rows.map(row => row.rowId);
      const write: DatabaseShardRowWrite = {
        addedRowIds: databaseShardAddedRowIds(
          stored.map(row => row.rowId),
          rowIds
        ),
        clocks: databaseShardDocWriteClocks(doc),
      };
      if (databaseShardRowWriteCondenseDue(plan.shard, write))
        markDatabaseShardCondenseDue(doc);
      Object.assign(
        plan.shard,
        context.updateLocalShardManifestEntry(
          plan.shard,
          rowIds,
          encodeStateAsUpdate(doc).byteLength,
          plan.shard.valueKeys,
          write
        )
      );
    }
    const nextManifest = createDatabaseShardManifest({
      ...manifest,
      shards: projected,
    });
    const stagedRoot = stage.doc(rootId);
    const block = rootBlock(
      stagedRoot,
      context._model.id ?? context.databaseId,
      context._model.store.workspace.id
    );
    const partMaps = publicationDirectoryPartMaps(stage);
    writeDatabaseRootDirectory(block, partMaps, nextManifest);
    if (input.rootFields) {
      const proxy = createYProxy<Record<string, unknown>>(block);
      for (const [key, value] of Object.entries(input.rootFields))
        proxy['prop:' + key] = value;
    }
    for (const { edit, shard, manifest: targetManifest } of inversePlans) {
      const targetRows = stage.doc(shard.serverDocId);
      const row = targetRows
        .getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)
        .get(edit.rowId);
      if (!(row instanceof YMap)) continue;
      const cell = row.get(edit.fieldId);
      const current =
        cell && typeof cell === 'object'
          ? (cell as { value?: unknown }).value
          : undefined;
      const remaining = (
        Array.isArray(current) ? current.map(String) : []
      ).filter(id => !edit.remove.includes(id));
      const value =
        edit.replace && edit.add.length
          ? [...new Set(edit.add)]
          : [...new Set([...remaining, ...edit.add])];
      row.set(edit.fieldId, { columnId: edit.fieldId, value });
      const updated = {
        ...shard,
        encodedBytes: encodeStateAsUpdate(targetRows).byteLength,
      };
      writeDatabaseRootDirectory(
        rootBlock(
          stage.doc(edit.target.docId),
          edit.target.databaseId,
          context._model.store.workspace.id
        ),
        partMaps,
        createDatabaseShardManifest({
          ...targetManifest,
          shards: targetManifest.shards.map(entry =>
            entry.shardId === shard.shardId ? updated : entry
          ),
        })
      );
    }
    if (context.isFileIndexDatabase()) {
      const registry = getFileIndexShardRegistry(
        stagedRoot,
        context.databaseId
      );
      for (const plan of planned)
        if (plan.rows.length) writeFileIndexShardEntry(registry, plan.shard);
        else registry.delete(plan.shard.shardId);
    }
    if (!input.createRows)
      input.rootMutation?.(stagedRootModel(context, stagedRoot, block, stage));
    for (const change of input.restore ?? [])
      applyDatabaseSemanticChange(stage.doc(change.docId), change);
    // Directory parts a write split off joined the stage as new documents.
    for (const id of stage.ids()) {
      if (allIds.includes(id)) continue;
      allIds.push(id);
      semanticsBefore.set(id, new Map());
      const doc = context.ensureWorkspaceDoc(id);
      if (!doc) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      if (doc.spaceDoc.clientID === stage.doc(id).clientID)
        throw new PublicationError('FORMAT_MISMATCH');
      live.set(id, doc.spaceDoc);
    }
    if (allIds.length > 64) throw new PublicationError('LIMIT_EXCEEDED');
    context.assertFileIndexRootSchemaWithinLimits({
      manifest: nextManifest,
      registryShards: projected,
    });
    const bytes = measureDatabaseCompute(compute, 'stage-encode', 0, () =>
      allIds.reduce((sum, id) => {
        const count = encodeStateAsUpdate(stage!.doc(id)).byteLength;
        if (count > DOCUMENT_PRODUCT_HARD_LIMIT_BYTES)
          throw new PublicationError('LIMIT_EXCEEDED');
        return sum + count;
      }, 0)
    );
    if (bytes > byteLimit) throw new PublicationError('LIMIT_EXCEEDED');
    const historyChanges = measureDatabaseCompute(
      compute,
      'history-after',
      0,
      () =>
        allIds.flatMap(id =>
          diffDatabaseSemantics(
            id,
            semanticsBefore.get(id)!,
            snapshotDatabaseSemantics(stage!.doc(id))
          )
        )
    );
    // A write that found nothing to change publishes nothing.
    if (!stage.changed()) return null;
    const receipt = await stage.commit(
      port,
      context._model.store.workspace.id,
      input.kind,
      [],
      signal
    );
    const committedStage = stage;
    const members = allIds.flatMap(id => {
      const doc = live.get(id);
      return doc
        ? [
            {
              doc,
              update: committedStage.update(id),
              origin: localPublicationApplyOrigin(receipt.operationId),
              directory: isDirectoryDoc(context, id),
            },
          ]
        : [];
    });
    measureDatabaseCompute(compute, 'live-apply', 0, () =>
      applyDatabaseLivePublication(
        members,
        () => context.refreshShardedRowsFromDocs(),
        () => bumpShardedRowsRevision(context)
      )
    );
    if (input.interactive !== false)
      publicationWriters
        .get(context.storageDoc)!
        .history.record(input.kind, historyChanges, input.maintenance);
    return receipt;
  } finally {
    stage?.destroy();
    for (const doc of opened) doc.destroy();
  }
}

function insertionChanges(
  context: DatabaseSourceRuntime,
  rows: readonly LocalRow[],
  index: number
): {
  changes: Map<DatabaseShardManifestEntry, LocalRowChange>;
  shards: DatabaseShardManifestEntry[];
} {
  findLocalRowShards.call(context, []);
  const manifest = requireCompleteShardDirectory(
    context._model,
    context.databaseId
  );
  let shards = [...manifest.shards];
  let target: DatabaseShardManifestEntry;
  let localIndex: number;
  if (context.isFileIndexDatabase()) {
    const prefix = getFileIndexWriterShardPrefix(
      context._model.store.doc.spaceDoc
    );
    const writer = selectFileIndexWriterShard(
      shards,
      prefix,
      context._fileIndexWriterShardId,
      manifest.targetBytes
    );
    target =
      writer ??
      context.createLocalShardEntry(
        nextFileIndexWriterShardId(context._model.store.doc.spaceDoc, shards)
      );
    if (!writer) shards.push(target);
    localIndex = target.rowCount;
  } else {
    const plan = planDatabaseRowInsertion(shards, index);
    target =
      shards[plan.shardIndex] ??
      context.createLocalShardEntry(
        databaseRowShardId(
          nextDatabaseShardIndex({
            nextShardIndex: manifest.nextShardIndex,
            shards,
          }) + 1
        )
      );
    if (plan.shardIndex < 0) shards.push(target);
    localIndex = plan.localIndex;
  }
  const doc = context.ensureShardObserved(target, 'write');
  const current = loadedLocalRows(context, target, doc);
  current.splice(localIndex, 0, ...rows);
  return { changes: new Map([[target, { doc, rows: current }]]), shards };
}

export function moveShardedRowInLocalDocs(
  this: DatabaseSourceRuntime,
  rowId: string,
  targetIndex: number
): void {
  const manifest = requireCompleteShardDirectory(this._model, this.databaseId);
  const source = this.resolveLocalRows([rowId], 'row-window').get(rowId);
  if (!source)
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Cannot move missing sharded database row "${rowId}".`
    );
  if (usesRowEditPort(this)) {
    const order = source.doc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray();
    const localIndex = order.indexOf(rowId);
    const sourceIndex = manifest.shards.findIndex(
      shard => shard.shardId === source.shard.shardId
    );
    const globalIndex =
      manifest.shards
        .slice(0, sourceIndex)
        .reduce((n, s) => n + s.rowCount, 0) + localIndex;
    const adjusted = globalIndex < targetIndex ? targetIndex - 1 : targetIndex;
    if (globalIndex === adjusted) return;
    const reduced = manifest.shards.map(s =>
      s.shardId === source.shard.shardId
        ? { ...s, rowCount: s.rowCount - 1 }
        : s
    );
    const plan = planDatabaseRowInsertion(reduced, adjusted);
    const destination = manifest.shards[plan.shardIndex]!;
    if (destination.shardId === source.shard.shardId) {
      void queueRowEdits(
        this,
        'move',
        new Map([
          [
            source.shard,
            {
              doc: source.doc,
              edits: [{ type: 'move', rowId, index: plan.localIndex }],
            },
          ],
        ])
      );
    } else {
      const doc = this.ensureShardObserved(destination, 'row-window');
      void queueRowEdits(
        this,
        'move',
        new Map([
          [
            source.shard,
            { doc: source.doc, edits: [{ type: 'delete', rowIds: [rowId] }] },
          ],
          [
            destination,
            {
              doc,
              edits: [
                {
                  type: 'transfer',
                  fromDocId: source.doc.id,
                  rowId,
                  index: plan.localIndex,
                },
              ],
            },
          ],
        ])
      );
    }
    return;
  }
  const sourceRows = loadedLocalRows(this, source.shard, source.doc);
  const localIndex = sourceRows.findIndex(row => row.rowId === rowId);
  const sourceIndex = manifest.shards.findIndex(
    shard => shard.shardId === source.shard.shardId
  );
  const globalIndex =
    manifest.shards
      .slice(0, sourceIndex)
      .reduce((n, shard) => n + shard.rowCount, 0) + localIndex;
  const adjusted = globalIndex < targetIndex ? targetIndex - 1 : targetIndex;
  if (globalIndex === adjusted) return;
  const [moving] = sourceRows.splice(localIndex, 1);
  const reduced = manifest.shards.map(shard =>
    shard.shardId === source.shard.shardId
      ? { ...shard, rowCount: shard.rowCount - 1 }
      : shard
  );
  const destinationIndex = planDatabaseRowInsertion(reduced, adjusted);
  const destination = manifest.shards[destinationIndex.shardIndex]!;
  const changes = new Map([
    [source.shard, { doc: source.doc, rows: sourceRows }],
  ]);
  const dest =
    destination.shardId === source.shard.shardId
      ? { doc: source.doc, rows: sourceRows }
      : (() => {
          const doc = this.ensureShardObserved(destination, 'row-window');
          return { doc, rows: loadedLocalRows(this, destination, doc) };
        })();
  // A row moved into another shard is written there anew and leaves its
  // removable cells behind, as the worker's transfer does.
  dest.rows.splice(
    destinationIndex.localIndex,
    0,
    destination.shardId === source.shard.shardId
      ? moving!
      : withoutRemovableCells(this, [moving!])[0]!
  );
  // Normalization creates new entry objects; identity is the shard ID.
  // A same-shard move updates the already registered source exactly once.
  if (destination.shardId !== source.shard.shardId)
    changes.set(destination, dest);
  void applyLocalRowChanges(this, changes, undefined, 'move');
}

/** Stored rows: the closed parts the head counts and the open last part. */
function databaseRowCount(runtime: DatabaseSourceRuntime): number {
  return requireShardDirectoryRowCount(runtime);
}

export function rowAddWithId(
  this: DatabaseSourceRuntime,
  rowId: string,
  insertPosition: InsertToPosition | number,
  actor: DataSourceWriteActor = 'user'
): { rowId: string; created: boolean } {
  this.assertFileIndexRowMutationAllowed(actor, 'Row creation');
  if (!rowId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Cannot create database row without an id.'
    );
  }
  if (this.rowExists(rowId)) {
    return { rowId, created: false };
  }
  assertDatabaseRowCountWithinLimit({
    source: 'Database',
    current: databaseRowCount(this),
    adding: 1,
  });
  const index = this.getShardedInsertIndex(insertPosition);
  if (usesRowEditPort(this)) {
    const manifest = requireCompleteShardDirectory(
      this._model,
      this.databaseId
    );
    const plan = planDatabaseRowInsertion(manifest.shards, index);
    const shard =
      manifest.shards[plan.shardIndex] ??
      this.createLocalShardEntry(
        databaseRowShardId(nextDatabaseShardIndex(manifest) + 1)
      );
    const doc = this.ensureShardObserved(shard, 'write');
    const existing = doc.spaceDoc
      .getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)
      .get(rowId);
    const cells: RowShardCells = {};
    const restored = isReadonlyYMap(existing);
    if (restored)
      existing.forEach((cell, id) => {
        cells[id] = normalizeRowShardCell(id, cell);
      });
    else
      Object.assign(
        cells,
        createDatabaseRowCreationSystemFieldCells({
          now: Date.now(),
          userId: this.currentWriterId(),
        })
      );
    void queueRowEdits(
      this,
      'create',
      new Map([
        [
          shard,
          {
            doc,
            edits: [
              {
                type: 'insert',
                rows: [{ rowId, cells }],
                index: plan.localIndex,
              },
            ],
          },
        ],
      ])
    );
    return { rowId, created: !restored };
  }
  const { changes, shards } = insertionChanges(this, [], index);
  const [shard, change] = [...changes][0]!;
  const existing = change.doc.spaceDoc
    .getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)
    .get(rowId);
  const cells: RowShardCells = {};
  const restored = isReadonlyYMap(existing);
  if (restored)
    existing.forEach((cell, id) => {
      cells[id] = normalizeRowShardCell(id, cell);
    });
  else
    Object.assign(
      cells,
      createDatabaseRowCreationSystemFieldCells({
        now: Date.now(),
        userId: this.currentWriterId(),
      })
    );
  const plan = planDatabaseRowInsertion(shards, index);
  change.rows.splice(
    this.isFileIndexDatabase() ? change.rows.length : plan.localIndex,
    0,
    { rowId, cells }
  );
  // 'create' registers the row as pending, so a cell written right after
  // it (calendar event, file index) finds the row before it is published.
  void applyLocalRowChanges(this, new Map([[shard, change]]), shards, 'create');
  return { rowId, created: !restored };
}

export function rowAddManyWithCellValues(
  this: DatabaseSourceRuntime,
  rows: readonly DatabaseRowCellValueInsert[],
  actor: DataSourceWriteActor = 'user'
): number {
  return measureDatabaseCompute(
    databaseComputePort(this._model.store.workspace),
    'intent-capture',
    rows.length,
    () => rowAddManyWithCellValuesImpl.call(this, rows, actor)
  );
}

function rowAddManyWithCellValuesImpl(
  this: DatabaseSourceRuntime,
  rows: readonly DatabaseRowCellValueInsert[],
  actor: DataSourceWriteActor = 'user'
): number {
  if (rows.length === 0) return 0;
  assertRowBatchWithinBudget(rows.length);
  this.assertFileIndexRowMutationAllowed(actor, 'Row creation');

  const plan = planRowSet(this, rows, [], actor);
  if (plan) void publishRowSet(this, plan);
  return rows.length;
}

type ShardRowEdits = Map<
  DatabaseShardManifestEntry,
  { doc: DatabaseShardDocument; edits: DatabaseRowEdit[] }
>;
type RowSetPlan = {
  kind: DatabaseRowMutation['kind'];
  removesRows: boolean;
} & (
  | { rowEdits: ShardRowEdits }
  | {
      changes: Map<DatabaseShardManifestEntry, LocalRowChange>;
      baseShards?: DatabaseShardManifestEntry[];
    }
);

function assertRowBatchWithinBudget(count: number): void {
  if (count > DATABASE_LARGE_DATA_POLICY.jobs.bulkRowLimit)
    throw new DatabaseBulkBudgetError(
      'rows',
      DATABASE_LARGE_DATA_POLICY.jobs.bulkRowLimit,
      count
    );
}

/**
 * Plans one publication of a sharded database that creates or changes the
 * upserted rows and removes the deleted ones. Only the shards holding these
 * rows (plus the append target for new rows) are read; on the row edit port
 * the edits name only these rows. Unknown rows to delete reject the whole
 * set; `null` means the set is empty.
 */
function planRowSet(
  context: DatabaseSourceRuntime,
  upserts: readonly DatabaseRowCellValueInsert[],
  deleteRowIds: readonly string[],
  actor: DataSourceWriteActor
): RowSetPlan | null {
  const manifest = requireCompleteShardDirectory(
    context._model,
    context.databaseId
  );
  // Fingerprints rule out new rows from metadata; only a row some
  // fingerprint may hold is checked against the shard documents.
  const seenRowIds = new Set<string>();
  const newRows: Array<{ rowId: string; cells: RowShardCells }> = [];
  const existingRows: DatabaseRowCellValueInsert[] = [];

  for (const row of upserts) {
    if (!row.rowId) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        'Cannot create database row without an id.'
      );
    }
    if (seenRowIds.has(row.rowId)) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Cannot create duplicate database row "${row.rowId}".`
      );
    }
    seenRowIds.add(row.rowId);

    if (
      databaseShardsMayContainRow(manifest.shards, row.rowId) &&
      context.rowExists(row.rowId)
    ) {
      existingRows.push(row);
      continue;
    }
    // The row the compared values belong to does not exist.
    if (row.expected) throw new PublicationError('LOCAL_CONFLICT');

    const cells: RowShardCells = {};
    for (const [propertyId, value] of Object.entries(row.values)) {
      const cell = context.resolveShardedCellForWrite(
        row.rowId,
        propertyId,
        value,
        actor
      );
      if (cell) {
        cells[propertyId] = cell;
      }
    }
    assertDatabaseRowWithinByteBudget({
      source: 'Database editor',
      rowId: row.rowId,
      cells: Object.entries(cells).map(
        ([id, cell]) => [id, cell.value] as const
      ),
    });
    newRows.push({ rowId: row.rowId, cells });
  }
  const deleting = new Set(deleteRowIds);
  for (const rowId of deleting)
    if (seenRowIds.has(rowId))
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Cannot change and delete database row "${rowId}" in one set.`
      );
  assertDatabaseRowCountWithinLimit({
    source: 'Database',
    current: databaseRowCount(context),
    adding: newRows.length,
  });

  const lineageColumns = databaseLineageColumns(context._model.props.columns);
  const preparedRows = newRows.map(row => ({
    rowId: row.rowId,
    cells: {
      ...createDatabaseRowCreationSystemFieldCells({
        now: Date.now(),
        userId: context.currentWriterId(),
      }),
      ...storedFormCells(lineageColumns, row.cells),
    },
  }));
  const kind = upserts.length ? 'create' : 'delete';
  const changedCells = (row: DatabaseRowCellValueInsert) => {
    const cells: RowShardCells = {};
    for (const [id, value] of Object.entries(row.values)) {
      const cell = context.resolveShardedCellForWrite(
        row.rowId,
        id,
        value,
        actor
      );
      if (cell) cells[id] = cell;
    }
    return cells;
  };
  if (usesRowEditPort(context)) {
    const rowEdits: ShardRowEdits = new Map();
    const editsOf = (
      shard: DatabaseShardManifestEntry,
      doc: DatabaseShardDocument
    ) => {
      let change = [...rowEdits].find(
        ([entry]) => entry.shardId === shard.shardId
      )?.[1];
      if (!change) {
        change = { doc, edits: [] };
        rowEdits.set(shard, change);
      }
      return change.edits;
    };
    if (preparedRows.length) {
      const shards = manifest.shards;
      const target =
        shards.at(-1) ??
        context.createLocalShardEntry(
          databaseRowShardId(nextDatabaseShardIndex(manifest) + 1)
        );
      editsOf(target, context.ensureShardObserved(target, 'write')).push({
        type: 'insert',
        rows: preparedRows,
        index: 'end',
      });
    }
    const addressed = context.resolveLocalRows(
      [...existingRows.map(row => row.rowId), ...deleting],
      'row-window'
    );
    for (const row of existingRows) {
      const located = addressed.get(row.rowId);
      if (!located) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      const edits = editsOf(located.shard, located.doc);
      const cells = changedCells(row);
      const raw = located.doc.spaceDoc
        .getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)
        .get(row.rowId);
      if (!isReadonlyYMap(raw))
        throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      Object.assign(
        cells,
        createDatabaseRowUpdateSystemFieldCells({
          now: Date.now(),
          userId: context.currentWriterId(),
          createdTimeValue: normalizeRowShardCell(
            DATABASE_ROW_CREATED_TIME_FIELD_ID,
            raw.get(DATABASE_ROW_CREATED_TIME_FIELD_ID)
          ).value,
          createdByValue: normalizeRowShardCell(
            DATABASE_ROW_CREATED_BY_FIELD_ID,
            raw.get(DATABASE_ROW_CREATED_BY_FIELD_ID)
          ).value,
          updatedByValue: normalizeRowShardCell(
            DATABASE_ROW_UPDATED_BY_FIELD_ID,
            raw.get(DATABASE_ROW_UPDATED_BY_FIELD_ID)
          ).value,
        })
      );
      edits.push({
        type: 'set-cells',
        rowId: row.rowId,
        cells,
        ...(row.expected ? { expected: row.expected } : {}),
      });
    }
    const removed = new Map<string, string[]>();
    for (const rowId of deleting) {
      const located = addressed.get(rowId)!;
      let rowIds = removed.get(located.shard.shardId);
      if (!rowIds) {
        rowIds = [];
        removed.set(located.shard.shardId, rowIds);
        editsOf(located.shard, located.doc).push({ type: 'delete', rowIds });
      }
      rowIds.push(rowId);
    }
    if (!rowEdits.size) return null;
    return { kind, removesRows: deleting.size > 0, rowEdits };
  }
  if (existingRows.some(row => row.expected))
    throw new PublicationError('FORMAT_MISMATCH');
  const { changes, shards: baseShards } = preparedRows.length
    ? insertionChanges(context, preparedRows, context.rowCount$.value)
    : {
        changes: new Map<DatabaseShardManifestEntry, LocalRowChange>(),
        shards: undefined,
      };
  const addressed = context.resolveLocalRows(
    [...existingRows.map(row => row.rowId), ...deleting],
    'row-window'
  );
  const changeOf = (
    shard: DatabaseShardManifestEntry,
    doc: DatabaseShardDocument
  ) => {
    let change = [...changes].find(
      ([entry]) => entry.shardId === shard.shardId
    )?.[1];
    if (!change) {
      change = { doc, rows: loadedLocalRows(context, shard, doc) };
      changes.set(shard, change);
    }
    return change;
  };
  for (const row of existingRows) {
    const located = addressed.get(row.rowId);
    if (!located)
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database row "${row.rowId}" is unavailable.`
      );
    const change = changeOf(located.shard, located.doc);
    const entry = change.rows.find(candidate => candidate.rowId === row.rowId)!;
    entry.cells = databaseRowCellsAfterWrite({
      lineageColumns,
      removable: new Set(),
      stored: entry.cells,
      written: changedCells(row),
    });
    Object.assign(
      entry.cells,
      createDatabaseRowUpdateSystemFieldCells({
        now: Date.now(),
        userId: context.currentWriterId(),
        createdTimeValue:
          entry.cells[DATABASE_ROW_CREATED_TIME_FIELD_ID]?.value,
        createdByValue: entry.cells[DATABASE_ROW_CREATED_BY_FIELD_ID]?.value,
        updatedByValue: entry.cells[DATABASE_ROW_UPDATED_BY_FIELD_ID]?.value,
      })
    );
  }
  for (const rowId of deleting) {
    const located = addressed.get(rowId)!;
    changeOf(located.shard, located.doc);
  }
  if (deleting.size)
    for (const change of changes.values())
      change.rows = change.rows.filter(row => !deleting.has(row.rowId));
  if (!changes.size) return null;
  return { kind, removesRows: deleting.size > 0, changes, baseShards };
}

/** One publication and one receipt for a planned row set. */
function publishRowSet(
  context: DatabaseSourceRuntime,
  plan: RowSetPlan,
  signal?: AbortSignal
): Promise<LocalPublicationReceipt | null> {
  const committed =
    'rowEdits' in plan
      ? submitRowEdits(context, plan.kind, plan.rowEdits, false, signal)
      : submitLocalRowChanges(
          context,
          plan.changes,
          plan.baseShards,
          plan.kind,
          undefined,
          false,
          signal
        );
  trackRowPublication(context, committed);
  scheduleShardMerges(
    context,
    plan.removesRows ? 'delete' : plan.kind,
    ('rowEdits' in plan ? plan.rowEdits : plan.changes).keys(),
    committed
  );
  return committed;
}

/**
 * Creates or changes `upserts` and deletes `deleteRowIds` of a sharded
 * database in one local publication: one operation, one receipt, all or
 * nothing. Resolves `null` for an empty set.
 */
export function commitRowSet(
  this: DatabaseSourceRuntime,
  set: DatabaseRowSet,
  signal?: AbortSignal,
  actor: DataSourceWriteActor = 'user'
): Promise<LocalPublicationReceipt | null> {
  try {
    assertRowBatchWithinBudget(set.upserts.length + set.deleteRowIds.length);
    if (set.upserts.length)
      this.assertFileIndexRowMutationAllowed(actor, 'Row creation');
    if (set.deleteRowIds.length)
      this.assertFileIndexRowMutationAllowed(actor, 'Row deletion');
    const plan = planRowSet(this, set.upserts, set.deleteRowIds, actor);
    return plan ? publishRowSet(this, plan, signal) : Promise.resolve(null);
  } catch (error) {
    return Promise.reject(error);
  }
}

export function normalizeShardedCellValueForProperty(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown
): unknown {
  if (this.isSpacialProperty(propertyId)) {
    return this.normalizeShardedSpacialCellValue(
      rowId,
      propertyId,
      propertyId,
      value
    );
  }
  const propertyType = this.propertyTypeGet(propertyId);
  if (!propertyType) {
    return value;
  }
  if (this.isSpacialProperty(propertyType)) {
    return this.normalizeShardedSpacialCellValue(
      rowId,
      propertyId,
      propertyType,
      value
    );
  }
  const meta = this.propertyMetaGet(propertyType);
  if (!meta) {
    return value;
  }
  const hasJsonCodec = typeof meta.config.rawValue.fromJson === 'function';
  return this.bindReactiveShardedCellValue({
    rowId,
    propertyId,
    propertyType,
    serialized: value,
    convert: () =>
      hasJsonCodec
        ? (fromJsonValue(meta as RawValueJsonConverter, {
            data: this.propertyDataGet(propertyId),
            dataSource: this.dataSource,
            value,
          }) ?? meta.config.rawValue.default())
        : value,
  });
}

export function shardedCellKey(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): string {
  return `${rowId}\u0000${propertyId}`;
}

export function serializedShardedCellEquals(
  this: DatabaseSourceRuntime,
  left: unknown,
  right: unknown
): boolean {
  if (Object.is(left, right)) return true;
  if (typeof left !== 'object' || typeof right !== 'object') return false;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

export function bindReactiveShardedCellValue(
  this: DatabaseSourceRuntime,
  {
    propertyId,
    propertyType,
    rowId,
    serialized,
    convert,
  }: {
    propertyId: string;
    propertyType: string;
    rowId: string;
    serialized: unknown;
    /** Builds the value from `serialized`; runs only when no bound value of
     * the same stored cell exists (a text value builds a document). */
    convert: () => unknown;
  }
): unknown {
  const meta = this.propertyMetaGet(propertyType);
  const onUpdate = meta?.config.rawValue.onUpdate;
  if (!onUpdate) {
    return convert();
  }

  const key = this.shardedCellKey(rowId, propertyId);
  const existing = this._reactiveShardedCellValues.get(key);
  if (
    existing &&
    this.serializedShardedCellEquals(existing.serialized, serialized)
  ) {
    return existing.value;
  }

  const value = convert();
  if (value == null) {
    return value;
  }
  existing?.dispose();

  let initialized = false;
  const disposable = onUpdate({
    value,
    data: this.propertyDataGet(propertyId),
    dataSource: this.dataSource,
    callback: () => {
      if (!initialized) return;
      this.queueReactiveShardedCellFlush(key);
    },
  });
  initialized = true;

  this._reactiveShardedCellValues.set(key, {
    dispose: () => disposable.dispose(),
    propertyId,
    propertyType,
    rowId,
    serialized,
    value,
  });
  return value;
}

export function queueReactiveShardedCellFlush(
  this: DatabaseSourceRuntime,
  key: string
): void {
  if (this._disposed) return;
  if (this._reactiveShardedCellFrames.has(key)) return;
  const frame = requestAnimationFrame(() => {
    this._reactiveShardedCellFrames.delete(key);
    this.flushReactiveShardedCellValue(key);
  });
  this._reactiveShardedCellFrames.set(key, frame);
}

export function flushReactiveShardedCellValue(
  this: DatabaseSourceRuntime,
  key: string
): void {
  const entry = this._reactiveShardedCellValues.get(key);
  if (!entry) return;
  const propertyType = this.propertyTypeGet(entry.propertyId);
  if (!propertyType) return;
  const serialized = this.serializeRawValueForProperty(
    propertyType,
    this.propertyDataGet(entry.propertyId),
    entry.value
  );
  if (this.serializedShardedCellEquals(entry.serialized, serialized)) {
    return;
  }

  entry.serialized = serialized;
  this.writeShardedCellToLocalDoc(entry.rowId, entry.propertyId, serialized);
}

export function normalizeShardedSpacialCellValue(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  propertyType: string,
  value: unknown
): unknown {
  const meta = this.propertyMetaGet(propertyType);
  if (!meta) {
    return value;
  }
  return this.bindReactiveShardedCellValue({
    rowId,
    propertyId,
    propertyType,
    serialized: value,
    convert: () =>
      fromJsonValue(meta as RawValueJsonConverter, {
        data: this.propertyDataGet(propertyType),
        dataSource: this.dataSource,
        value,
      }) ?? value,
  });
}

function publicationWriter(context: DatabaseSourceRuntime): {
  tail: Promise<unknown>;
  history: DatabaseMutationHistory;
} {
  let writer = publicationWriters.get(context.storageDoc);
  if (!writer) {
    const history = new DatabaseMutationHistory(
      mutation => context.commitRowMutation(mutation),
      run => {
        const current = publicationWriter(context);
        const docIds: string[] = [];
        const ready = current.tail.then(() =>
          run(mutation => {
            docIds.push(...mutationDocIds(mutation));
            return commitRowMutationNow.call(context, mutation);
          })
        );
        publicationDocIds.set(ready, docIds);
        current.tail = ready.then(
          () => {},
          () => {}
        );
        trackRowPublication(context, ready);
        return ready;
      }
    );
    writer = { tail: Promise.resolve(), history };
    publicationWriters.set(context.storageDoc, writer);
    context.storageDoc.provider.container.override(
      StoreHistoryOverride,
      () => history
    );
  }
  return writer;
}

/** Apply only durable worker receipts; no planning or storage work runs here. */
export function applyImportedCommits(
  this: DatabaseSourceRuntime,
  commits: readonly DatabaseImportCommit[]
): void {
  const writer = publicationWriter(this);
  const root = this._model.store.doc.spaceDoc;
  const rootId = this._model.store.id;
  // Inspect the durable manifest in a detached document. Resolve the bounded
  // visible projection before publishing any part of the receipt to the UI.
  const projection = new YDoc({ guid: rootId });
  let directory: ReturnType<typeof readImportedDirectory>;
  try {
    applyUpdate(projection, encodeStateAsUpdate(root));
    for (const commit of commits)
      for (const member of commit.updates)
        if (member.docId === rootId) applyUpdate(projection, member.update);
    directory = readImportedDirectory(
      this,
      projection,
      this._model.id ?? this.databaseId,
      part => {
        for (const commit of commits)
          for (const member of commit.updates)
            if (member.docId === part.guid) applyUpdate(part, member.update);
      }
    );
  } finally {
    projection.destroy();
  }
  const { manifest } = directory;
  const parts = new Set(directory.partDocIds);
  const members: DatabaseLivePublicationMember[] = [];
  const byDoc = new Map(
    manifest.shards.map(shard => [shard.serverDocId, shard])
  );
  const initial = new Set(
    this.getManifestShardsForRowsWindow(
      manifest,
      0,
      INITIAL_DATABASE_ROW_WINDOW_LIMIT
    ).map(shard => shard.serverDocId)
  );
  for (const commit of commits) {
    for (const member of commit.updates) {
      if (member.docId === rootId) {
        members.push({
          doc: root,
          update: member.update,
          origin: localPublicationApplyOrigin(commit.receipt.operationId),
          directory: true,
        });
        continue;
      }
      if (parts.has(member.docId)) {
        // Part documents not open here load their committed state on demand.
        const part = this._model.store.workspace.getDoc(member.docId);
        if (part)
          members.push({
            doc: part.spaceDoc,
            update: member.update,
            origin: localPublicationApplyOrigin(commit.receipt.operationId),
            directory: true,
          });
        continue;
      }
      const shard = byDoc.get(member.docId);
      if (!shard) throw new PublicationError('FORMAT_MISMATCH');
      const existing = this.getExistingShardDoc(shard);
      // Already observed rows must stay current even outside the first page.
      // Closed shards retain their committed bytes in storage, with no UI Y.Doc.
      if (!existing && !initial.has(member.docId)) continue;
      const doc = existing ?? this.ensureWorkspaceDoc(member.docId);
      if (!doc) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
      members.push({
        doc: doc.spaceDoc,
        update: member.update,
        origin: localPublicationApplyOrigin(commit.receipt.operationId),
      });
    }
    writer.history.record('create', commit.historyChanges);
  }
  applyDatabaseLivePublication(
    members,
    () => this.refreshShardedRowsFromDocs(),
    () => bumpShardedRowsRevision(this)
  );
}

/** Read one archived packet at a time. Only visible documents cross to the UI;
 * the undo entry owns references, never an import-sized array of cell values. */
export async function applyArchivedImport(
  this: DatabaseSourceRuntime,
  importer: DatabaseImportPort,
  jobId: string,
  commits: readonly DatabaseImportCommitSummary[]
): Promise<void> {
  if (!commits.length) return;
  const rootId = this._model.store.id;
  await importer.retainHistory(jobId);
  publicationWriter(this).history.recordDeferred('create', {
    count: commits.length,
    read: index => {
      const commit = commits[index];
      if (!commit) throw new RangeError(`Import history packet ${index}`);
      return importer.history({ jobId, packet: commit.packet });
    },
    release: () => importer.releaseHistory(jobId),
  });
  const origin = localPublicationApplyOrigin(
    commits.at(-1)!.receipt.operationId
  );
  // The root and the part documents the import wrote, in one projection
  // read. Rows appended into the open last part leave the root head alone.
  const workspaceId = this._model.store.workspace.id;
  const writtenDirectory = [
    ...new Set(commits.flatMap(commit => commit.docIds)),
  ].filter(
    docId =>
      docId === rootId ||
      classifyPageSubdocId(docId, workspaceId)?.kind ===
        'database-directory-part'
  );
  const directoryUpdates = await importer.projection({
    jobId,
    docIds: writtenDirectory,
  });
  const rootUpdates = directoryUpdates.filter(u => u.docId === rootId);
  const root = this._model.store.doc.spaceDoc;
  const databaseId = this._model.id ?? this.databaseId;
  const projection = new YDoc({ guid: rootId });
  let manifest: DatabaseShardManifest;
  let partUpdates: typeof directoryUpdates = [];
  try {
    applyUpdate(projection, encodeStateAsUpdate(root));
    for (const update of rootUpdates) applyUpdate(projection, update.update);
    const importedParts = new Set(
      readPublicationDirectoryPartDocIds(
        rootBlock(projection, databaseId, workspaceId)
      )
    );
    partUpdates = directoryUpdates.filter(u => importedParts.has(u.docId));
    manifest = readImportedDirectory(this, projection, databaseId, part => {
      for (const update of partUpdates)
        if (update.docId === part.guid) applyUpdate(part, update.update);
    }).manifest;
  } finally {
    projection.destroy();
  }
  const visible = new Set(
    this.getManifestShardsForRowsWindow(
      manifest,
      0,
      INITIAL_DATABASE_ROW_WINDOW_LIMIT
    ).map(s => s.serverDocId)
  );
  const docs = new Map(
    manifest.shards
      .filter(s => visible.has(s.serverDocId) || this.getExistingShardDoc(s))
      .map(s => [s.serverDocId, s])
  );
  const updates = docs.size
    ? await importer.projection({ jobId, docIds: [...docs.keys()] })
    : [];
  const members: DatabaseLivePublicationMember[] = rootUpdates.map(update => ({
    doc: root,
    update: update.update,
    origin,
    directory: true,
  }));
  // Part documents not open here load their committed state on demand.
  for (const update of partUpdates) {
    const part = this._model.store.workspace.getDoc(update.docId);
    if (part)
      members.push({
        doc: part.spaceDoc,
        update: update.update,
        origin,
        directory: true,
      });
  }
  for (const update of updates) {
    const shard = docs.get(update.docId)!;
    const doc =
      this.getExistingShardDoc(shard) ?? this.ensureWorkspaceDoc(update.docId);
    if (!doc) throw new PublicationError('INCOMPLETE_LOCAL_BASE');
    members.push({ doc: doc.spaceDoc, update: update.update, origin });
  }
  applyDatabaseLivePublication(
    members,
    () => this.refreshShardedRowsFromDocs(),
    () => bumpShardedRowsRevision(this)
  );
}

/**
 * Invalidates the row count and windows of `context` at once, inside the
 * signal batch of a live publication whose directory just landed.
 */
function bumpShardedRowsRevision(context: DatabaseSourceRuntime): void {
  context._shardedRowsRevision.value += 1;
}
